import { Injectable } from "@angular/core";
import { PRIMITIVE, ServiceMessageBrokerFactory } from "shared/provider-webworker";

import { ModelUpdate } from "../../impl/ModelUpdate";
import { PrimaryBlotterLookupService } from "../../impl/PrimaryBlotterLookupService";
import { IBenchmarkDataPromiseLookupService } from "../IBenchmarkDataPromiseLookupService";

import { Subject, Observable, Subscription } from "rxjs";

import { RawDataBmarkClientOrdersAdapter } from "../../../mediator/adapter/RawDataBmarkClientOrdersAdapter";
import { SysFeatures } from "../../../model/core/SysFeatures";
import { BFXNotificationType } from "../../../model/core/notifications/BFXNotificationType";
import { BFXAppNotificationType } from "../../../model/core/notifications/BFXAppNotificationType";
import { BFXNotificationSubType } from "../../../model/core/notifications/BFXNotificationSubType";
import { OrderDataPrivatizationService } from "../../../model/privatization/OrderDataPrivatizationService";

import { CubiqWorker } from "../../../worker/CubiqWorker";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { UserLookupService } from "./UserLookupService";
import { UserImpl } from "shared/cubiq-model-multi-asset";
import { TraderGroupLookupService } from "./TraderGroupLookupService";
import { TradingGroupImpl } from "shared/cubiq-model-multi-asset";
import { ActiveUserProfilePromiseService } from "shared/auth-services";
import { ActiveUser } from "../../../model/core/ActiveUser";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { BmarkFixingData } from "../../../model/benchmarks/BmarkFixingData.model";
import { BmarkFixingDefinition } from "../../../model/benchmarks/BmarkFixingDefinition.model";
import { BmarkFixingType } from "../../../model/benchmarks/enums/BmarkFixingType.enum";
import { BmarkFixingEventOperation } from "../../../model/benchmarks/enums/BmarkFixingEventOperation.enum";
import { BmarkOrderDataEventOperation } from "../../../model/benchmarks/enums/BmarkOrderDataEventOperation.enum";
import { SystemConfigPromiseService } from "shared/auth-services";
import { filter } from "rxjs";
import { NotificationEventProcessorService } from "../../../model/notifications/NotificationEventProcessorService";
import { BmarkOrderDataEventSource } from "shared/cubiq-services/model/benchmarks/enums/BmarkOrderDataEventSource.enum";
import { BmarkAllocationMetaData } from "shared/cubiq-services/model/benchmarks/BmarkAllocationMetaData.model";
import { NotificationService } from "../../notification/impl/NotificationService";
import { BenchmarkFixingPrimaryLookupService } from "./BenchmarkFixingPrimaryLookupService";
import { BmarkOrderData } from "shared/cubiq-services/model/benchmarks/BmarkOrderData.model";
import {
  BmarkOrderDataEvent,
  BmarkOrderDataEventPayload,
} from "../../../model/benchmarks/BmarkOrderDataEvent.model";
import { BmarkFixingEvent } from "../../../model/benchmarks/BmarkFixingEvent.model";
import { OrderDataModelUpdateSource } from "./OrderDataPromiseLookupService";

import {
  HedgeOrderDataLookupService,
  BLOTTERS_HEDGE_ORDERS,
  HedgeOrderQueuedEvent,
  HEDGE_ORDER_NEW_ORDER_STATES,
} from "./HedgeOrderDataLookupService";

import {
  AllBenchmarksRequestService,
  AllBenchmarksResponse,
} from "../../../action/impl/AllBenchmarksRequestService";
import { AllBenchmarksFixingSummariesService } from "../../../action/impl/AllBenchmarksFixingSummariesService";
import { AllBenchmarksFixingStatusService } from "../../../action/impl/AllBenchmarksFixingStatusService";
import { BfxConfigService } from "shared/auth-services/system-config/bfx-config.service";
import { HEDGE_ORDER_ACTIVE_STATES, HEDGE_ORDER_REJECT_STATES } from "../../../../auth-services";
import { EVENT_TYPE_INFLIGHT_CHECK_STATE } from "./OrderDataPrimaryLookupService";
import { BFXNotificationEventType } from "shared/cubiq-services/model/core/notifications/BFXNotificationEventType";
import { TRANSFERED_OUT_OF_VISIBILITY } from "shared/cubiq-services/model/orders/TransferUtils";

@Injectable()
export class HedgeOrderDataPrimaryLookupService
  extends PrimaryBlotterLookupService<string, CubiqTS.OrderDataImpl>
  implements IBenchmarkDataPromiseLookupService
{
  private activeUser: ActiveUser;
  // Message ports for Replica
  protected bmarkUpdateEventPorts: Array<MessagePort> = new Array<MessagePort>();
  // Primary subjects
  protected bmarkFixingsUpdateSubject: Subject<any> = new Subject<any>();
  protected bmarkOrderDataUpdateSubject: Subject<any> = new Subject<any>();
  // neutral subjects
  protected interventionEventSubject: Subject<CubiqTS.InterventionEventImpl> =
    new Subject<CubiqTS.InterventionEventImpl>();
  // Fixings Purge configs
  protected fixingsRecentOrdersIdleOffset: number = 30 * 1000; // 30 secs
  protected fixingsRecentPurgeInterval: number = 30 * 1000; // 30 secs
  protected fixingsRecentPurgeOffset: number = 168 * 3600 * 1000; // 168 hours
  protected defaultSweepOffset: number = 10 * 60 * 1000; // just a fallback, 10 mins
  // Info needed for the recent purge
  protected displayedRecentFixing: string = null;
  // Needed for Fixing expiration
  protected fixingDefinitionMap: Map<string, BmarkFixingDefinition> = new Map<
    string,
    BmarkFixingDefinition
  >();
  // Bmark Order Data UPDATE Event internal configs
  protected control: {
    queueFixingEvents: boolean;
    queueOrderDataEvents: boolean;
    attachImplToEvent: boolean; // controls attachment of OrderDataImpl to appropriate events
  } = {
    queueFixingEvents: true,
    queueOrderDataEvents: true,
    attachImplToEvent: false,
  };

  private fixingEventQueue: Array<any> = new Array<any>();
  private orderDataEventQueue: Array<any> = new Array<any>();

  private outputLog: boolean = true; // for dev testing - to be deleted at some point

  private retrievalRecentFixingId: string | null = null;
  private retrievalRecentProcessingQueue: Set<string> = new Set<string>();
  private retrievalRecentFixingTimestamp: number | null = null;
  private retrievalRecentFixingProcessTimeOffset: number = 60 * 1000; // 1 min

  protected base: HedgeOrderDataLookupService;
  private configurationSubscription: Subscription;
  private recentFixingsSubscription: Subscription;

  constructor(
    // @Inject(forwardRef(() => HedgeOrderDataLookupService))
    base: HedgeOrderDataLookupService,
    protected factory: ServiceMessageBrokerFactory,
    protected cubiqWorker: CubiqWorker,
    protected userService: UserLookupService,
    protected traderGroupService: TraderGroupLookupService,
    protected dataAdapter: RawDataBmarkClientOrdersAdapter,
    private privatizationService: OrderDataPrivatizationService,
    private activeUserService: ActiveUserProfilePromiseService,
    private sysConfigService: SystemConfigPromiseService,
    private fixingDefinitionDataService: BenchmarkFixingPrimaryLookupService,
    private notificationProcessor: NotificationEventProcessorService,
    notifService: NotificationService,
    protected fixingSummariesService: AllBenchmarksFixingSummariesService,
    protected fixingStatusService: AllBenchmarksFixingStatusService,
    protected allBenchmarksRequestService: AllBenchmarksRequestService,
    private _configService: BfxConfigService,
  ) {
    super(
      base,
      factory,
      CubiqTS.OrderDataImpl,
      cubiqWorker,
      "HedgeOrderDataImpl",
      CubiqTS.OrderLifeCycleEventImpl,
      dataAdapter,
      notifService,
    );
    this.base = base;
    this.broker.registerMethod(
      "getAllActiveBmarkFixings",
      [],
      this.getAllActiveBmarkFixings.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "getAllRecentBmarkFixings",
      [],
      this.getAllRecentBmarkFixings.bind(this),
      PRIMITIVE,
    );
    //this.broker.registerMethod('getBmarkFixingData', [PRIMITIVE,PRIMITIVE], this.getBmarkFixingData.bind(this), PRIMITIVE);
    //this.broker.registerMethod('clearDisplayedFixing', [], this.clearDisplayedFixing.bind(this), PRIMITIVE);
    this.broker.registerMethod(
      "initBmarkUpdateService",
      [PRIMITIVE],
      this.initBmarkUpdateService.bind(this),
      null,
      false,
    );
    //
    this.broker.registerMethod(
      "getBmarkOrderDataForOrderIds",
      [PRIMITIVE],
      this.getBmarkOrderDataForOrderIds.bind(this),
      this.VALUE,
    );
    //this.broker.registerMethod('getBmarkAdaptedDataForOrderIds', [PRIMITIVE], this.getBmarkAdaptedDataForOrderIds.bind(this), PRIMITIVE);
    this.broker.registerMethod(
      "getBmarkOrderDataForFixingId",
      [PRIMITIVE, PRIMITIVE],
      this.getBmarkOrderDataForFixingId.bind(this),
      this.VALUE,
    );
    //
    this.broker.registerMethod(
      "bmarkManagerReady",
      [],
      this.bmarkManagerReady.bind(this),
      PRIMITIVE,
    );

    this.subscribeToConfiguration();

    logger.out.info("HedgeOrderDataPrimaryLookupService: primeDataProfile waiting on isAuth()");
    Promise.all([
      cubiqWorker.isAuth(`${this.constructor.name}`),
      this.privatizationService.isReady(),
      this.activeUserService.getActiveUserData(),
      this.sysConfigService.isPrimed(),
      this.primeRecentFixings(),
    ]).then(
      ([
        ,
        ,
        // skip
        // skip
        activeUser,
      ]) => {
        this.logBmarkInfo("HedgeOrderDataPrimaryLookupService: primeDataProfile isAuth() resolved");
        this.activeUser = activeUser;
        this.base.setActiveUser(this.activeUser);

        // Order Data for Active Fixings Request
        let desc = new CubiqTS.DataProfileDescriptorImpl();
        desc.filterName = "Hedge Orders";
        let bmarkActiveOrderDataProfileRequest = new CubiqTS.DataProfileRequestImpl();
        bmarkActiveOrderDataProfileRequest.profileDescriptors = [];
        bmarkActiveOrderDataProfileRequest.profileDescriptors.push(desc);

        cubiqWorker.primeDataProfile(BLOTTERS_HEDGE_ORDERS, CubiqTS.OrderDataImpl.name).subscribe(
          (orderData: CubiqTS.OrderDataImpl[]) => {
            if (
              orderData instanceof Array &&
              orderData.length > 0 &&
              orderData[0] instanceof CubiqTS.OrderDataImpl
            ) {
              this.processHedgeOrderData(orderData);
              this.processForPrivatization(orderData);
              this.base.prime(orderData, false);
              this.devTestDisplayOrderData(orderData);
            } else if (orderData instanceof Array && orderData.length === 0) {
              // no benchmarks -> do nothing
            } else {
              logger.out.error(
                `ERROR: ${this.constructor.name} Failed to process Active Benchmark Orders! Active Fixings =`,
                this.base.getAllActiveFixings(),
              );
            }
          },
          (ex: Error) => {
            logger.out.error(
              `ERROR: ${this.constructor.name}: Failed to prime order data for Active fixings: `,
              ex,
            );
            this._primed.reject(ex);
          },
          () => {
            this.scheduleCheckRecentOrdersPurge();
            // Initial state update of fixings
            this.primeFixingStatus().then(() => {
              this.base.sortFixingMap();
              this.reportFixingsListSummary();
              logger.out.info(
                `${this.constructor.name}: HedgeOrderDataPrimaryLookupService set PRIMED!`,
              );

              this.base.prime([], true);
              this.primed = true;
              this._primed.resolve(true);
            });
          },
        );
      },
    );

    this.base.flashManager.flashUpdateSubject.subscribe(this.onOrderFlash.bind(this));

    // Bmark Life Cycle
    this.cubiqWorker
      .getEncodableObservable(CubiqTS.OrderLifeCycleEventImpl.name)
      .pipe(filter(($event) => this.benchmarksFilter($event as CubiqTS.OrderLifeCycleEventImpl)))
      .subscribe(($event: CubiqTS.OrderLifeCycleEventImpl) => {
        let state =
          $event != null && $event.orderData != null && $event.orderData.orderState != null
            ? CubiqTS.OrderState[$event.orderData.orderState]
            : null;
        this.logBmarkInfo(`CubiqTS.OrderLifeCycleEventImpl state: ${state} => `, $event, true);
        if ($event != null && this.control.queueOrderDataEvents) {
          this.logBmarkInfo(
            `QUEUED Event ${
              CubiqTS.OrderLifeCycleEventImpl.name
            } fixing ${BmarkFixingData.generateFixingDataLogMsg($event.orderData)}`,
            $event,
            true,
          );
          this.orderDataEventQueue.push(
            new HedgeOrderQueuedEvent($event, this.processBmarkOrderLifecycleEvent.bind(this)),
          );
        } else {
          if (
            $event != null &&
            $event.orderData != null &&
            $event.orderData.allocationData != null
          ) {
            this.processBmarkOrderLifecycleEvent($event);
            this.devTestDisplayOrderData([$event.orderData]);
          } else {
            logger.out.error(`Failed to process OL event for order ${$event?.orderData?.orderId}`);
          }
        }
      });

    this.cubiqWorker
      .getEncodableObservable(CubiqTS.UnsolicitedCancelLifeCycleEventImpl.name)
      .pipe(
        filter(($event) =>
          this.benchmarksFilter($event as CubiqTS.UnsolicitedCancelLifeCycleEventImpl),
        ),
      )
      .subscribe(($event: CubiqTS.UnsolicitedCancelLifeCycleEventImpl) => {
        if ($event != null && this.control.queueOrderDataEvents) {
          this.logBmarkInfo(
            `QUEUED Event ${CubiqTS.UnsolicitedCancelLifeCycleEventImpl.name}`,
            $event,
            true,
          );
          this.orderDataEventQueue.push(
            new HedgeOrderQueuedEvent($event, this.processUnsolCanLifecycleEvent.bind(this)),
          );
        } else {
          this.logBmarkInfo(
            `PROCESS Event ${CubiqTS.UnsolicitedCancelLifeCycleEventImpl.name}`,
            $event,
            true,
          );
          this.processUnsolCanLifecycleEvent($event);
        }
      });

    // Bmark Lock event
    this.cubiqWorker
      .getEncodableObservable(CubiqTS.BenchmarkLockLifeCycleEventImpl.name)
      .pipe(
        filter(($event) =>
          this.benchmarksFilter($event as CubiqTS.BenchmarkLockLifeCycleEventImpl),
        ),
      )
      .subscribe(($event: CubiqTS.BenchmarkLockLifeCycleEventImpl) => {
        this.logBmarkInfo("CubiqTS.BenchmarkLockLifeCycleEventImpl ", $event, true);
        if ($event != null && this.control.queueOrderDataEvents) {
          this.logBmarkInfo(
            `QUEUED Event ${CubiqTS.BenchmarkLockLifeCycleEventImpl.name}`,
            $event,
            true,
          );
          this.orderDataEventQueue.push(
            new HedgeOrderQueuedEvent($event, this.processBmarkLockLifecycleEvent.bind(this)),
          );
        } else {
          this.processBmarkLockLifecycleEvent($event);
        }
      });
    // Bmark Xfer event
    this.cubiqWorker
      .getEncodableObservable(CubiqTS.XferEventImpl.name)
      .pipe(filter(($event) => this.benchmarksFilter($event as CubiqTS.XferEventImpl)))
      .subscribe(($event: CubiqTS.XferEventImpl) => {
        this.logBmarkInfo("CubiqTS.XferEventImpl ", $event, true);
        if ($event != null && this.control.queueOrderDataEvents) {
          this.logBmarkInfo(`QUEUED Event ${CubiqTS.XferEventImpl.name}`, $event, true);
          this.orderDataEventQueue.push(
            new HedgeOrderQueuedEvent($event, this.processBmarkXferEvent.bind(this)),
          );
        } else {
          this.processBmarkXferEvent($event);
        }
      });

    // Bulk - Bmark Life Cycle
    this.cubiqWorker
      .getEncodableObservable(CubiqTS.BulkOrderLifeCycleEventImpl.name)
      .pipe(
        filter(($event) => this.benchmarksFilter($event as CubiqTS.BulkOrderLifeCycleEventImpl)),
      )
      .subscribe(($event: CubiqTS.BulkOrderLifeCycleEventImpl) => {
        this.logBmarkInfo("CubiqTS.BulkOrderLifeCycleEventImpl ", $event, true);
        if ($event != null && this.control.queueOrderDataEvents) {
          this.logBmarkInfo(
            `QUEUED Event ${CubiqTS.BulkOrderLifeCycleEventImpl.name}`,
            $event,
            true,
          );
          this.orderDataEventQueue.push(
            new HedgeOrderQueuedEvent($event, this.processBmarkBulkOrderLifecycleEvent.bind(this)),
          );
        } else {
          this.processBmarkBulkOrderLifecycleEvent($event);
        }
      });

    // Bmark Intervention Event
    this.cubiqWorker
      .getEncodableObservable(CubiqTS.InterventionEventImpl.name)
      .pipe(filter(($event) => this.benchmarksFilter($event as CubiqTS.InterventionEventImpl)))
      .subscribe(($event: CubiqTS.InterventionEventImpl) => {
        this.logBmarkInfo("CubiqTS.InterventionEventImpl ", $event, true);
        if ($event != null && this.control.queueOrderDataEvents) {
          this.logBmarkInfo(`QUEUED Event ${CubiqTS.InterventionEventImpl.name}`, $event, true);
          this.orderDataEventQueue.push(
            new HedgeOrderQueuedEvent($event, this.processBmarkInterventionEvent.bind(this)),
          );
        } else {
          this.processBmarkInterventionEvent($event);
        }

        this.interventionEventSubject.next($event);
      });

    // Bmark Order (Fixing) Update - for fill error
    this.cubiqWorker
      .getEncodableObservable(CubiqTS.FixingOrderUpdateEventImpl.name)
      .subscribe(($event: CubiqTS.FixingOrderUpdateEventImpl) => {
        this.logBmarkInfo("CubiqTS.FixingOrderUpdateEventImpl ", $event, false);
        if ($event != null && this.control.queueOrderDataEvents) {
          this.logBmarkInfo(
            `QUEUED Event ${CubiqTS.FixingOrderUpdateEventImpl.name}`,
            $event,
            false,
          );
          this.orderDataEventQueue.push(
            new HedgeOrderQueuedEvent($event, this.processBmarkFixingOrderUpdate.bind(this)),
          );
        } else {
          this.processBmarkFixingOrderUpdate($event);
        }
      });

    // Fixing Event
    this.cubiqWorker
      .getEncodableObservable(CubiqTS.FixingEventImpl.name)
      .subscribe(($event: CubiqTS.FixingEventImpl) => {
        logger.out.info(
          `${this.constructor.name} received FixingEventImpl ` +
            `fixingState: ${CubiqTS.FixingState[$event?.fixingState]} ` +
            `[this.control.queueFixingEvents:${this.control.queueFixingEvents}]`,
        );
        if ($event != null && this.control.queueFixingEvents) {
          this.fixingEventQueue.push(
            new HedgeOrderQueuedEvent($event, this.processFixingUpdate.bind(this)),
          );
        } else {
          this.processFixingUpdate($event);
        }
      });

    // Fixing Definition
    this.cubiqWorker
      .getEncodableObservable(CubiqTS.BenchmarkFixingImpl.name)
      .subscribe(($event: CubiqTS.BenchmarkFixingImpl) => {
        if ($event != null && this.control.queueFixingEvents) {
          this.logBmarkInfo(`QUEUED Event ${CubiqTS.BenchmarkFixingImpl.name}`, $event, false);
          this.fixingEventQueue.push(
            new HedgeOrderQueuedEvent($event, this.updateFixingDefinitionMap.bind(this)),
          );
        } else {
          this.logBmarkInfo(
            "FIXING DEFINITION UPDATE = CubiqTS.BenchmarkFixingImpl: ",
            $event,
            false,
          );
          this.updateFixingDefinitionMap($event);
        }
      });

    this.cubiqWorker
      .getEncodableObservable(CubiqTS.PauseLifeCycleEventImpl.name)
      .subscribe(($event: CubiqTS.PauseLifeCycleEventImpl) => {
        if ($event != null && this.control.queueFixingEvents) {
          this.logBmarkInfo(`QUEUED Event ${CubiqTS.PauseLifeCycleEventImpl.name}`, $event, false);
          this.fixingEventQueue.push(
            new HedgeOrderQueuedEvent($event, this.onPauseLifeCycleEvent.bind(this)),
          );
        } else {
          this.logBmarkInfo(
            "FIXING DEFINITION UPDATE = CubiqTS.PauseLifeCycleEventImpl: ",
            $event,
            false,
          );
          this.onPauseLifeCycleEvent($event);
        }
      });

    // Observe application messages
    this.cubiqWorker
      .getEncodableObservable(CubiqTS.ApplicationMessageEventImpl.name)
      .subscribe(this.processInflightApplicationMessage.bind(this));

    this.recentFixingsSubscription = this.allBenchmarksRequestService.recentFixing$.subscribe(
      (fixingData: BmarkFixingData) => {
        if (!this.base.hasRecentFixing(fixingData.id)) {
          this.base.addRecentFixing(fixingData);
        }
      },
    );
  }

  ngOnDestroy() {
    if (null != this.configurationSubscription) {
      this.configurationSubscription.unsubscribe();
    }

    if (null != this.recentFixingsSubscription) {
      this.recentFixingsSubscription.unsubscribe();
    }
  }

  /**********************************************************************/
  /* INITIALIZATION */
  private subscribeToConfiguration(): void {
    this.configurationSubscription = this._configService.uiConfig$.subscribe((config) => {
      this.outputLog = config.general.debug;
    });
  }

  onPauseLifeCycleEvent($event: CubiqTS.PauseLifeCycleEventImpl) {
    if ($event.orders != null) {
      $event.orders.forEach((incoming: CubiqTS.PauseOrderData) => {
        let order: BmarkOrderData = this.base.getBmarkOrderDataForOrderId(
          incoming.orderId.toString(),
        );

        if (order != null) {
          order.pauseState =
            incoming.pauseState === null || incoming.pauseState === undefined
              ? CubiqTS.PauseState.NOT_PAUSED
              : incoming.pauseState;
          this.base.updateItem(order);

          let eventObject: any = Object.assign(Object.create(BmarkOrderData.prototype), order, {
            source: OrderDataModelUpdateSource.OrderLifeCycleEvent,
            evtTypeId: $event.eventTypeId,
          });
          if (
            incoming.errors != null &&
            Array.isArray(incoming.errors.errorMessages) &&
            incoming.errors.errorMessages.length > 0
          ) {
            eventObject.errors = incoming.errors;
          }
          this.modelUpdateEventSubject.next(eventObject);

          let eventPayloadData = new BmarkOrderDataEventPayload(
            order.orderId.toString(),
            order.clOrdID,
            BmarkOrderDataEventSource.OrderLifeCycleEvent,
            order,
          );
          eventPayloadData.bmarkOrderData = this.base.preprocessOrderData(order);
          eventPayloadData.state = order.orderState;
          eventPayloadData.evtType = $event.eventTypeId;
          eventPayloadData.messageId = $event.messageId;
          eventPayloadData.lockEvent = false;
          //                    eventPayloadData.user = ($event..lockUserId) ? $event.lockUserId.toString() : null;

          let orderDataEvent = new BmarkOrderDataEvent();
          orderDataEvent.addOperations([BmarkOrderDataEventOperation.UPDATE]);
          orderDataEvent.payload = eventPayloadData;

          this.fireBmarkOrderDataUpdateEvent(orderDataEvent);
        }
      });
    }
  }

  private devTestDisplayOrderData(
    orderData: Array<CubiqTS.OrderDataImpl>,
    optionalText: string = "",
  ): void {
    if (this.outputLog) {
      if (
        orderData instanceof Array &&
        orderData.length > 0 &&
        orderData[0] instanceof CubiqTS.OrderDataImpl
      ) {
        for (let orderItem of orderData) {
          if (
            orderItem instanceof CubiqTS.OrderDataImpl &&
            Array.isArray(orderItem.allocationData) &&
            orderItem.allocationData.length > 0
          ) {
            let result: string = optionalText;
            if (orderItem.allocationData.length === 1) {
              result +=
                "=(ROOT)-> " + this.devTestDisplayAllocationData(orderItem.allocationData[0]);
            } else if (orderItem.allocationData.length > 1) {
              for (let allocationItem of orderItem.allocationData) {
                result += "=(ROOT)-> " + this.devTestDisplayAllocationData(allocationItem);
              }
            }
            logger.out.info(
              `${this.constructor.name}: Fixing[${BmarkFixingData.generateFixingDataLogMsg(
                orderItem,
              )}]: Order(${orderItem.orderId})${result}`,
            );
          } else {
            logger.out.error(
              `${this.constructor.name}: devTestDisplay(): Invalid AllocationData`,
              orderItem,
            );
          }
        }
      } else {
        logger.out.error(
          `${this.constructor.name}: devTestDisplay(): Invalid OrderData`,
          orderData,
        );
      }
    }
  }

  private devTestDisplayAllocationData(aloData: CubiqTS.AllocationDataImpl): string {
    let result: string = "";
    if (aloData instanceof CubiqTS.AllocationDataImpl) {
      let allocationString =
        `[${aloData.allocationId}-${CubiqTS.AllocationType[aloData.allocationType]}] (${
          aloData.baseCcy + aloData.nonbaseCcy
        })=> ` +
        `Trade: ${
          aloData.fillDetails instanceof CubiqTS.LastFillDetailsImpl &&
          typeof aloData.fillDetails.dealId === "string"
            ? aloData.fillDetails.dealId
            : "None"
        } | `;

      if (this.base.allocationHasMetaData(aloData.allocationId.toString())) {
        let allocationMetaData = this.base.getAllocationMetaData(aloData.allocationId.toString());
        let visibleOnItsOwn =
          allocationMetaData instanceof BmarkAllocationMetaData &&
          typeof allocationMetaData.visibleOnItsOwn === "boolean"
            ? allocationMetaData.visibleOnItsOwn
              ? "Yes"
              : "No"
            : "Unknown";
        let visibleInContext =
          allocationMetaData instanceof BmarkAllocationMetaData
            ? this.base.isLocalAllocationVisibleInContext(allocationMetaData.allocationIdString)
              ? "Yes"
              : "No"
            : "Unknown";
        allocationString += `Visible: alone/context = ${visibleOnItsOwn}/${visibleInContext}; `;
      } else {
        allocationString += `Visible: No meta-data; `;
      }

      if (
        Array.isArray(aloData.componentAllocations) &&
        aloData.componentAllocations.length > 0 &&
        aloData.componentAllocations[0] instanceof CubiqTS.AllocationDataImpl
      ) {
        if (aloData.componentAllocations.length === 1) {
          result =
            allocationString + this.devTestDisplayAllocationData(aloData.componentAllocations[0]);
        } else if (aloData.componentAllocations.length > 0) {
          result = allocationString;
          for (let allocationItem of aloData.componentAllocations) {
            result += this.devTestDisplayAllocationData(allocationItem);
          }
        }
      } else {
        result = allocationString;
      }
    } else {
      result = "Invalid allocationData";
    }
    return result;
  }

  /* ******************************************************* */
  /* BENCHMARKS EVENT FILTER */
  // Returns TRUE if event is for Benchmark, FALSE otherwise
  private benchmarksFilter($event: any): boolean {
    let result: boolean = false;
    if ($event instanceof CubiqTS.ConcreteEncodable) {
      switch ($event.encodableType) {
        case "com.bcdfx.model.event.lifecycle.OrderLifeCycleEventImpl": {
          let event: CubiqTS.OrderLifeCycleEventImpl = $event as CubiqTS.OrderLifeCycleEventImpl;
          result =
            event.orderData instanceof CubiqTS.OrderDataImpl &&
            ((event.orderData.orderType === CubiqTS.OrderType.BENCHMARK &&
              event.orderData.fixingDetails.bmOrderType ===
                CubiqTS.BenchmarkOrderType.PositionOrder) ||
              event.orderData.orderType === CubiqTS.OrderType.BMALGO);
          break;
        }
        case "com.bcdfx.model.event.lifecycle.UnsolicitedCancelLifeCycleEventImpl": {
          let event: CubiqTS.UnsolicitedCancelLifeCycleEventImpl =
            $event as CubiqTS.UnsolicitedCancelLifeCycleEventImpl;
          event.orders.forEach((orderData: CubiqTS.OrderData) => {
            result =
              orderData instanceof CubiqTS.OrderDataImpl &&
              ((orderData.orderType === CubiqTS.OrderType.BENCHMARK &&
                orderData.fixingDetails.bmOrderType === CubiqTS.BenchmarkOrderType.PositionOrder) ||
                orderData.orderType === CubiqTS.OrderType.BMALGO);
          });

          break;
        }
        case "com.bcdfx.model.event.lifecycle.BenchmarkLockLifeCycleEventImpl": {
          result = true; // BenchmarkLockLifeCycleEventImpl => lock event type only for benchmarks
          break;
        }
        case "com.bcdfx.model.event.lifecycle.XferEventImpl": {
          $event = $event as CubiqTS.XferEventImpl;
          result =
            Array.isArray($event.orderData) &&
            $event.orderData[0] instanceof CubiqTS.OrderDataImpl &&
            (($event.orderData[0].orderType === CubiqTS.OrderType.BENCHMARK &&
              $event.orderData[0].fixingDetails.bmOrderType ===
                CubiqTS.BenchmarkOrderType.PositionOrder) ||
              $event.orderData[0].orderType === CubiqTS.OrderType.BMALGO);
          break;
        }
        case "com.bcdfx.model.InterventionEventImpl": {
          $event = $event as CubiqTS.InterventionEventImpl;
          result =
            $event.orderData instanceof CubiqTS.OrderDataImpl &&
            (($event.orderData.orderType === CubiqTS.OrderType.BENCHMARK &&
              $event.orderData.fixingDetails.bmOrderType ===
                CubiqTS.BenchmarkOrderType.PositionOrder) ||
              $event.orderData.orderType === CubiqTS.OrderType.BMALGO);
          break;
        }
        case "com.bcdfx.model.event.lifecycle.BulkOrderLifeCycleEventImpl": {
          let bulkOrderEvent: CubiqTS.BulkOrderLifeCycleEventImpl =
            $event as CubiqTS.BulkOrderLifeCycleEventImpl;

          // If it's previsibility bulk event it is a benchmark bulk event
          result = bulkOrderEvent.eventTypeId === CubiqTS.FxLifeCycleEventType.BULK_PREVISIBILITY;

          if (Array.isArray(bulkOrderEvent.events)) {
            return result || this.benchmarksFilter(bulkOrderEvent.events[0]);
          }
          break;
        }
        case "com.bcdfx.model.event.lifecycle.FixingOrderUpdateEventImpl": {
          $event = $event as CubiqTS.FixingOrderUpdateEventImpl;
          result =
            $event.orderData instanceof CubiqTS.OrderDataImpl &&
            ($event.orderData.orderType === CubiqTS.OrderType.BENCHMARK ||
              $event.orderData.orderType === CubiqTS.OrderType.BMALGO);
          break;
        }
      }
    }
    //this.logBmarkInfo(`${this.constructor.name}: Event FILTER => ${result ? 'BENCHMARK (PROCESS)': 'NOT BENCHMARK (IGNORE)'} for ${$event.encodableType} => $event:`, $event);
    return result;
  }

  /* ******************************************************* */
  /* BENCHMARK FIXINGS - UPDATE SUBJECT STREAM */

  public initBmarkUpdateService(port: MessagePort): void {
    this.bmarkUpdateEventPorts.push(port);
  }

  public getBmarkFixingsUpdateSubject(): Subject<any> {
    return this.bmarkFixingsUpdateSubject;
  }

  protected fireBmarkFixingEvent(operation: BmarkFixingEventOperation, payload: any): any {
    if (operation in BmarkFixingEventOperation) {
      // this.updateDisplayColor(payload); // A1 alternative fixing color update (not in use for now)
      let fixingEvent = new BmarkFixingEvent(operation, payload);

      this.logBmarkInfo(
        `${this.constructor.name} Sending BMARK-FIXING-EVENT [${
          BmarkFixingEventOperation[fixingEvent.operation]
        }] => `,
        fixingEvent,
      );
      this.bmarkFixingsUpdateSubject.next(fixingEvent);
    } else {
      logger.out.error(
        `ERROR: ${this.constructor.name} fireBmarkFixingEvent(): Invalid fixing event operation or id`,
      );
    }
  }

  /* DO NOT DELETE (for now) */
  /**
   * Currently the update of fixing tile color is performed from bmark-fixings-manager.service since it looks to be more efficient.
   * The method below can be used on 5 invocation points (commented out, marked with A(n)) in this service to update the fixing tile color
   * before it gets to bmark-fixings-manager.service. However this approach doubles the number of invocations in this setting, but it could be
   * a better solution when the code is refactored so that fixings are managed by an independent service, instead of current situation where
   * ClientOrdersLookup and this (HedgeOrdersLookup) maintain each own fixings repositories.
   */
  /*  protected updateDisplayColor(payload: any): void {
        if(payload != null){
            if(payload instanceof BmarkFixingData){
                payload.updateDisplayColor();
            }else if(Array.isArray(payload) && payload.length > 0 && payload[0] instanceof BmarkFixingData){
                for(let payloadItem of payload){
                    payloadItem.updateDisplayColor();
                }
            }
        }
    }
    */

  /* ******************************************************* */
  /* BENCHMARK FIXINGS - PUBLIC API  */
  public bmarkManagerReady(): Promise<boolean> {
    return new Promise<any>((res, rej) => {
      this.flushQueuedFixingUpdateEvents();
      this.flushQueuedBmarkOrderDataEvents();
      res(true);
    });
  }

  public getAllActiveBmarkFixings(): Promise<Array<BmarkFixingData>> {
    return new Promise<any>((res, rej) => {
      let activeFixings = this.base.getAllActiveFixings();
      //this.updateDisplayColor(activeFixings); // A2 alternative fixing color update (not in use for now)
      res(activeFixings);
    });
  }

  public getAllRecentBmarkFixings(): Promise<Array<BmarkFixingData>> {
    return new Promise<Array<BmarkFixingData>>((res, rej) => {
      res(this.base.getAllRecentFixings());
    });
  }

  public getBmarkFixingDataAsEvent(fixingId: string, fixingType: BmarkFixingType): boolean {
    // DEV-FIXING - remove log
    //logger.out.warn(`DEV-FIXING: HEDGE-LOOKUP -> [${fixingId}] Processing request, data as EVENT!`)
    if (
      typeof fixingId === "string" &&
      fixingType in BmarkFixingType &&
      this.base.hasFixingId(fixingId)
    ) {
      switch (fixingType) {
        case BmarkFixingType.ACTIVE:
          this.displayedRecentFixing = null;
          let activeFixingData = this.base.getActiveFixingData(fixingId);
          this.fireBmarkFixingEvent(BmarkFixingEventOperation.UPDATE_DATA, activeFixingData);
          return true;
        case BmarkFixingType.RECENT:
          let fixingData = this.base.getRecentFixingData(fixingId);
          if (fixingData instanceof BmarkFixingData) {
            this.displayedRecentFixing = fixingId;
            this.retrieveOrderDataForRecentFixing(fixingId);
            return true;
          }
      }
    }
    return false;
  }

  public setDisplayedRecentFixing(fixingId: string): void {
    if (
      typeof fixingId == "string" &&
      fixingId != this.displayedRecentFixing &&
      this.base.hasRecentFixing(fixingId)
    ) {
      // displayed fixing has changed, set purge timestamp to purge orders after offset
      this.setPurgeTimestamp(this.displayedRecentFixing);
      //
      this.displayedRecentFixing = fixingId;
    }
  }

  public clearDisplayedRecentFixing(): void {
    this.setPurgeTimestamp(this.displayedRecentFixing);
    this.displayedRecentFixing = null;
  }

  private setPurgeTimestamp(fixingId: string): void {
    if (typeof fixingId === "string" && this.base.hasRecentFixing(fixingId)) {
      let fixingDataToPurgeOrders = this.base.getRecentFixingData(fixingId);
      if (fixingDataToPurgeOrders != null) {
        fixingDataToPurgeOrders.purgeTimestamp = parseInt(CubiqTS.moment().format("x"));
      }
    }
  }

  /* ******************************************************* */
  /* BENCHMARK ORDER DATA - PUBLIC API  */

  public getBmarkOrderDataForOrderIds(ids: Array<string>): Promise<Array<BmarkOrderData>> {
    return new Promise<Array<BmarkOrderData>>((res, rej) => {
      this.isPrimed().then(() => {
        res(this.base.getBmarkOrderDataForOrderIds(ids));
      });
    });
  }

  public getBmarkOrderDataForFixingId(
    fixingId: string,
    fixingType: BmarkFixingType,
  ): Promise<Array<BmarkOrderData>> | null {
    return new Promise<Array<BmarkOrderData>>((res, rej) => {
      if (typeof fixingId === "string" && fixingType in BmarkFixingType) {
        switch (fixingType) {
          case BmarkFixingType.ACTIVE: {
            let activeOrders = this.base.getBmarkOrderDataForFixingId(fixingId, fixingType);
            if (Array.isArray(activeOrders)) res(activeOrders);
            else res([]);
            break;
          }
          case BmarkFixingType.RECENT: {
            this.retrieveOrderDataForRecentFixing(fixingId, true).then((values) => {
              res(Array.isArray(values) ? values : []);
            });
            break;
          }
          case BmarkFixingType.ALL: {
            let activeOrders: Array<BmarkOrderData> = this.base.getBmarkOrderDataForFixingId(
              fixingId,
              BmarkFixingType.ACTIVE,
            );
            this.retrieveOrderDataForRecentFixing(fixingId, true).then((recentOrders) => {
              let allOrders: Array<BmarkOrderData> = Array.isArray(activeOrders)
                ? Array.isArray(recentOrders)
                  ? activeOrders.concat(...recentOrders)
                  : activeOrders
                : recentOrders;

              res(allOrders);
            });
            break;
          }
          default: {
            res([]);
            break;
          }
        }
      } else {
        res([]);
      }
    });
  }

  protected primeRecentFixings(): Promise<void> {
    return new Promise<void>((res, rej) => {
      Promise.all([
        this.sysConfigService.getSystemConfig(SysFeatures.fixingsRecentPurgeOffset),
        this.createFixingDefinitionMap(),
      ]).then(
        ([fixingsRecentPurgeOffset]) => {
          this.fixingsRecentPurgeOffset = parseInt(fixingsRecentPurgeOffset) * 3600 * 1000;
          // this.fixingDefinitionMap set in this createFixingDefinitionMap() side effect
          this.fixingSummariesService.primeHedgeOrder().then(
            (recentFixings) => {
              if (recentFixings != null && Array.isArray(recentFixings.results)) {
                this.processRecentBenchmarkOrderData(recentFixings.results);
              }
              res();
            },
            (err?) => {
              rej(err);
            },
          );
        },
        (err?) => {
          rej(err);
        },
      );
    });
  }

  /* ******************************************************* */
  /* SERVER REQUEST  */
  protected primeFixingStatus(): Promise<boolean> {
    return new Promise<boolean>((res, rej) => {
      let statusRequest = new CubiqTS.FixingStatusRequestImpl();

      this.fixingStatusService.primeHedgeOrder().then(
        (response) => {
          //this.logBmarkInfo(`${this.constructor.name}: ${this.constructor.name} Retreive Updates Response: `, response);
          if (response instanceof CubiqTS.FixingStatusResponseImpl) {
            if (response.fixingEvent instanceof Array && response.fixingEvent.length > 0) {
              for (let fixingEvent of response.fixingEvent) {
                this.processFixingUpdate(fixingEvent);
              }
            }
            res(true);
          } else {
            logger.out.error(
              `ERROR: ${this.constructor.name}.updateFixingsStatus(): Invalid response`,
            );
            res(false);
          }
        },
        (reject) => {
          res(false);
        },
      );
    });
  }

  protected retrieveUpdateForFixingId(fixingId: string): void {
    if (typeof fixingId === "string" && this.base.hasFixingId(fixingId)) {
      let fixingData = this.base.getFixingData(fixingId);
      let timeCode = new CubiqTS.BMTimeCodeImpl();
      timeCode.code = fixingData.code;
      timeCode.time = CubiqTS.moment(fixingData.fixingTimestamp);
      let statusRequest = new CubiqTS.FixingStatusRequestImpl();
      statusRequest.bMTimeCode = timeCode;

      this.cubiqWorker.postServiceRequest(statusRequest).then((response: any) => {
        logger.out.warn(
          `${this.constructor.name}: updateFixingStatusWithId(): Response for '${fixingId}': `,
          response,
        ); //${id}
        if (response instanceof CubiqTS.FixingStatusResponseImpl) {
          if (response.fixingEvent instanceof Array && response.fixingEvent.length > 0) {
            for (let fixingEvent of response.fixingEvent) {
              this.processFixingUpdate(fixingEvent);
            }
          }
        }
      });
    }
  }

  /*********************************************************
   *   This method performs retrieval of bmark data from AllBenchmarksRequestService (either cached or server).
   *   The method is designed for 2 purposes (based on promiseRetrieval input), first (default) it will retrieve the bmark order
   *   data and send an fixing update event  which will be consumed by the fixing manager (the returned promise is not used), and
   *   second as recent fixings data provider for the blotter which uses the data returned in the promise.
   *
   * The method also supports queueing of multiple requests for event based fixing update delivery.
   */
  protected retrieveOrderDataForRecentFixing(
    fixingId: string,
    resolveAsPromise: boolean = false,
  ): Promise<Array<BmarkOrderData>> {
    return new Promise<Array<BmarkOrderData>>((res, rej) => {
      let fixingData: BmarkFixingData = null;

      /*
             Fixing might be marked as recent via processing the fixing event on the oposing lookup while the 
             event has still not been processed by this one. In this case check for its presence in active fixings
             and see if the instance has it marked as recent. 
            */
      if (fixingId != null) {
        fixingData = this.base.getRecentFixingData(fixingId);

        if (fixingData == null) {
          let activeFixingDataId: string = BmarkFixingData.getIdAsActive(fixingId);
          if (activeFixingDataId != null) {
            let activeFixingData: BmarkFixingData =
              this.base.getActiveFixingData(activeFixingDataId);
            if (activeFixingData != null && activeFixingData.type === BmarkFixingType.RECENT) {
              fixingData = activeFixingData;
            }
          }
        }
      }

      if (fixingData != null) {
        let invokingFixingId = fixingId;
        fixingId = !resolveAsPromise ? this.setRetrievalRecentFixingId(fixingId) : fixingId;

        logger.out.info(
          `Recent Fixing Retrieval: BMARK-HEDGE-LOOKUP -> [${invokingFixingId}] = Retrieval INVOKED ${
            fixingId != invokingFixingId ? "on [" + fixingId + "]" : ""
          }` +
            ` => resolveAsPromise: ${resolveAsPromise ? "YES" : "NO"} | Retrieval status: ` +
            `${
              this.retrievalRecentFixingId === null
                ? "EMPTY"
                : this.retrievalRecentFixingId === fixingId
                ? "WORKING"
                : "BUSY | Queue size: " + this.retrievalRecentProcessingQueue.size
            }`,
        );

        if (fixingId != null) {
          let toBeDeleteFixing: BmarkFixingData | null = null;
          let bmarkOrderDataset = [];
          this.isPrimed().then((value) => {
            this.allBenchmarksRequestService.requestHedgeOrders(fixingData).subscribe(
              (orderData: AllBenchmarksResponse) => {
                logger.out.info(
                  `Recent Fixing Retrieval: BMARK-HEDGE-LOOKUP -> [${fixingId}] Dataset returned: size ${orderData.length}`,
                );
                if (orderData.responseLength === 0) {
                  let recentFixingData: any = this.base.getRecentFixingData(fixingId);
                  if (recentFixingData) toBeDeleteFixing = BmarkFixingData.retain(recentFixingData);
                } else if (Array.isArray(orderData)) {
                  this.base.setOrderIdsToRecentFixing(fixingId, orderData);
                  this.processForPrivatization(orderData);
                  this.base.prime(orderData);
                  orderData = orderData.map((order: CubiqTS.OrderDataImpl) =>
                    this.base.getItem(order.orderId.toString()),
                  );
                  bmarkOrderDataset = bmarkOrderDataset.concat(orderData);

                  if (orderData.length > 0) {
                    this.devTestDisplayOrderData(orderData);
                  }
                }
              },
              (ex: Error) => {
                logger.out.error(
                  `ERROR: ${this.constructor.name}: Failed to retrieve order data for Recent fixings: `,
                  ex,
                );
                this.clearRetrievalRecentFixingId();
                res(null);
              },
              () => {
                logger.out.info(
                  `Recent Fixing Retrieval: BMARK-HEDGE-LOOKUP -> Promise Complete [${fixingId}] | Promise Retrieval: ${
                    resolveAsPromise ? "YES" : "NO"
                  }` +
                    ` Fixing retrieval match:  ${
                      this.retrievalRecentFixingId === fixingId ? "YES" : "NO"
                    } | Retrieval request [${this.retrievalRecentFixingId}] `,
                );

                if (!resolveAsPromise || this.retrievalRecentFixingId === fixingId) {
                  this.clearRetrievalRecentFixingId();
                  if (toBeDeleteFixing == null) {
                    logger.out.info(
                      `Recent Fixing Retrieval: BMARK-HEDGE-LOOKUP -> Promise Complete [${fixingId}] = UPDATE `,
                    );
                    this.fireBmarkFixingEvent(BmarkFixingEventOperation.UPDATE_DATA, fixingData);
                  } else {
                    logger.out.info(
                      `Recent Fixing Retrieval: BMARK-HEDGE-LOOKUP -> Promise Complete [${fixingId}] = DELETING `,
                    );
                    this.fireBmarkFixingEvent(BmarkFixingEventOperation.REMOVE, toBeDeleteFixing);
                    BmarkFixingData.release(toBeDeleteFixing);
                    this.base.deleteRecentFixing(fixingId);
                    bmarkOrderDataset = [];
                  }
                }

                res(bmarkOrderDataset);

                let nextQueuedFixingId = this.getNextRetrievalFixingFromQueue();
                if (
                  nextQueuedFixingId != null &&
                  (!resolveAsPromise || this.retrievalRecentFixingId === fixingId)
                ) {
                  logger.out.info(
                    `Recent Fixing Retrieval: BMARK-HEDGE-LOOKUP -> [${nextQueuedFixingId}] = SET FOR RETRIEVAL ( from queue) | Queue size: ${this.retrievalRecentProcessingQueue.size}`,
                  );
                  this.retrieveOrderDataForRecentFixing(nextQueuedFixingId);
                  return;
                }
              },
            );
          });
        }
      } else {
        res([]);
      }
    });
  }

  /* RETRIEVAL QUEUE MANAGEMENT - START  */
  private setRetrievalRecentFixingId(fixingId: string): string | null {
    if (typeof fixingId === "string") {
      let now = parseInt(CubiqTS.moment().format("x"));
      if (this.retrievalRecentFixingId == null) {
        this.retrievalRecentFixingId = fixingId;
        this.retrievalRecentFixingTimestamp = now + this.retrievalRecentFixingProcessTimeOffset;
        logger.out.info(
          `Recent Fixing Retrieval: HEDGE-LOOKUP -> [${fixingId}] = SET FOR RETRIEVAL (queue empty)`,
        );
        return fixingId;
      } else if (now > this.retrievalRecentFixingTimestamp) {
        // cancel current fixing, time expired
        let canceledRecentFixingId = this.retrievalRecentFixingId;
        this.clearRetrievalRecentFixingId();
        logger.out.info(
          `Recent Fixing Retrieval: BMARK-HEDGE-LOOKUP -> [${fixingId}] = CANCEL/INTERRUPT (resolution taking too long)`,
        );
        // process queue
        let nextQueuedFixingId = this.getNextRetrievalFixingFromQueue(); // take from queue
        if (nextQueuedFixingId != null) {
          // queue was not empty
          this.retrievalRecentFixingId = nextQueuedFixingId;
          this.retrievalRecentFixingTimestamp = now + this.retrievalRecentFixingProcessTimeOffset;
          logger.out.info(
            `Recent Fixing Retrieval: BMARK-HEDGE-LOOKUP -> [${nextQueuedFixingId}] = SET FOR RETRIEVAL (after cancel, from queue) | Queue size: ${this.retrievalRecentProcessingQueue.size}`,
          );
          if (
            fixingId != canceledRecentFixingId &&
            fixingId != nextQueuedFixingId &&
            !this.retrievalRecentProcessingQueue.has(fixingId)
          ) {
            // add new to queue if appropriate
            this.retrievalRecentProcessingQueue.add(fixingId);
            logger.out.info(
              `Recent Fixing Retrieval: BMARK-HEDGE-LOOKUP -> [${fixingId}] = ADD TO QUEUE`,
            );
          }
          return nextQueuedFixingId;
        } else if (fixingId != canceledRecentFixingId) {
          // nothing in queue
          this.retrievalRecentFixingId = fixingId;
          this.retrievalRecentFixingTimestamp = now + this.retrievalRecentFixingProcessTimeOffset;
          logger.out.info(
            `Recent Fixing Retrieval: BMARK-HEDGE-LOOKUP -> [${fixingId}] = SET FOR RETRIEVAL (after cancel, queue empty)`,
          );
          return fixingId;
        }
      } else if (this.retrievalRecentFixingId === fixingId) {
        logger.out.info(
          `Recent Fixing Retrieval: BMARK-HEDGE-LOOKUP -> [${fixingId}] = IGNORE (being processed atm)`,
        );
      } else if (
        fixingId != this.retrievalRecentFixingId &&
        !this.retrievalRecentProcessingQueue.has(fixingId)
      ) {
        this.retrievalRecentProcessingQueue.add(fixingId);
        logger.out.info(
          `Recent Fixing Retrieval: BMARK-HEDGE-LOOKUP -> [${fixingId}] = ADD TO QUEUE`,
        );
      } else {
        logger.out.info(
          `Recent Fixing Retrieval: BMARK-HEDGE-LOOKUP -> [${fixingId}] = IGNORE (already in queue)`,
        );
      }
    }
    return null;
  }

  private clearRetrievalRecentFixingId(): void {
    this.retrievalRecentFixingId = null;
    this.retrievalRecentFixingTimestamp = null;
  }

  private getNextRetrievalFixingFromQueue(): string | null {
    let nextQueuedFixingId = null;
    if (this.retrievalRecentProcessingQueue.size > 0) {
      nextQueuedFixingId = this.retrievalRecentProcessingQueue.values().next().value;
      this.retrievalRecentProcessingQueue.delete(nextQueuedFixingId);
    }
    return nextQueuedFixingId;
  }
  /* RETRIEVAL QUEUE MANAGEMENT - END  */

  public processRecentBenchmarkOrderData(recentBmarks: Array<CubiqTS.BMTimeCodeImpl>): void {
    if (
      recentBmarks instanceof Array &&
      recentBmarks.length > 0 &&
      recentBmarks[0] instanceof CubiqTS.BMTimeCodeImpl
    ) {
      for (let recentBmark of recentBmarks) {
        this.createRecentFixingsFromBMTimeCode(recentBmark);
      }
      this.fireBmarkFixingEvent(
        BmarkFixingEventOperation.UPDATE_RECENT_LIST,
        this.base.getAllRecentFixings(),
      );
    }
  }

  private createRecentFixingsFromBMTimeCode(recentBmark: CubiqTS.BMTimeCodeImpl): void {
    // try{
    if (
      recentBmark instanceof CubiqTS.BMTimeCodeImpl &&
      typeof recentBmark.code === "string" &&
      recentBmark.time != null
    ) {
      let newFixing = BmarkFixingData.create(
        recentBmark.code,
        recentBmark.time,
        BmarkFixingType.RECENT,
        this.getFixingDefinitionCopy(recentBmark.code),
      );
      let newFixingId = newFixing.id;

      if (!this.shouldBePurgedOffset(recentBmark.time.format("x"))) {
        if (!this.base.hasRecentFixing(newFixingId)) {
          this.base.addRecentFixing(newFixing);
          this.allBenchmarksRequestService.addRecentFixing(newFixing);
          this.fireBmarkFixingEvent(BmarkFixingEventOperation.ADD, newFixing);
        } else {
          logger.out.error(
            `ERROR: ${this.constructor.name} createRecentFixingsFromBMTimeCode(): Recent fixing [${newFixingId}] already exists`,
          );
        }
      } else {
        this.logBmarkInfo(
          `${this.constructor.name} Recent Fixing [${newFixingId}] ignored on creation - too old`,
        );
      }
    } else {
      logger.out.error(
        `ERROR: ${this.constructor.name} createRecentFixingsFromBMTimeCode(): Invalid recent fixing`,
      );
    }
    // }catch(ex){
    //     logger.out.error(ex);
    // }
  }

  /* ACTIVE */
  public processHedgeOrderData(orderDataSet: Array<CubiqTS.OrderDataImpl>): void {
    let activeTouched = false;
    let recentTouched = false;

    if (
      orderDataSet instanceof Array &&
      orderDataSet.length > 0 &&
      orderDataSet[0] instanceof CubiqTS.OrderDataImpl
    ) {
      for (let i = 0; i < orderDataSet.length; i++) {
        let orderData = orderDataSet[i];
        activeTouched = true;
        // logger.out.info(`${this.constructor.name}.processHedgeOrderData() for orderId: ${orderData.orderId.toString()}`);
        this.createFixingsFromOrderData(orderData);
      }
    }
    if (activeTouched)
      this.fireBmarkFixingEvent(
        BmarkFixingEventOperation.UPDATE_ACTIVE_LIST,
        this.base.getAllActiveFixings(),
      );

    if (recentTouched)
      this.fireBmarkFixingEvent(
        BmarkFixingEventOperation.UPDATE_RECENT_LIST,
        this.base.getAllRecentFixings(),
      );
  }

  private createFixingsFromOrderData(orderData: CubiqTS.OrderDataImpl): string | null {
    let retFixingId: string = null;

    if (!this.hasBmarkFixingData(orderData)) {
      return null;
    }

    const fixingTimestamp = parseInt(orderData.fixingDate.format("x"));
    const shouldBePurged: boolean = this.shouldBePurgedOffset(fixingTimestamp);
    const isActive =
      this.isOrderDataActive(fixingTimestamp, orderData.fixingSourceCd) && !shouldBePurged;
    const orderId = orderData.orderId.toString();

    let activeFixingId = BmarkFixingData.id(
      orderData.fixingSourceCd,
      orderData.fixingDate,
      BmarkFixingType.ACTIVE,
    );

    let recentFixingId = BmarkFixingData.id(
      orderData.fixingSourceCd,
      orderData.fixingDate,
      BmarkFixingType.RECENT,
    );

    let hasActiveFixing = this.base.hasActiveFixing(activeFixingId);
    let hasRecentFixing = this.base.hasRecentFixing(recentFixingId);

    let operation: BmarkFixingEventOperation = null;
    let fixingData: BmarkFixingData = null;
    let fixingDef = this.getFixingDefinitionCopy(orderData.fixingSourceCd);

    this.allBenchmarksRequestService.invalidateById(activeFixingId);
    this.allBenchmarksRequestService.invalidateById(recentFixingId);

    if (shouldBePurged) {
      return null;
    }

    if (isActive) {
      if (
        hasActiveFixing &&
        !this.base.hasOrderIdInActiveFixing(activeFixingId, orderData.orderId.toString())
      ) {
        this.base.addOrderToActiveFixing(activeFixingId, orderData.orderId.toString(), orderData);
        operation = BmarkFixingEventOperation.UPDATE_DATA;
      } else if (hasActiveFixing) {
        this.base.updateOrderInActiveFixing(
          activeFixingId,
          orderData.orderId.toString(),
          orderData,
        );
        operation = BmarkFixingEventOperation.UPDATE_DATA;
      } else {
        this.base.addActiveFixing(
          BmarkFixingData.create(
            orderData.fixingSourceCd,
            orderData.fixingDate,
            BmarkFixingType.ACTIVE,
            fixingDef,
          ),
        );
        this.base.addOrderToActiveFixing(activeFixingId, orderData.orderId.toString(), orderData);
        operation = BmarkFixingEventOperation.ADD;
        retFixingId = activeFixingId;
      }

      fixingData = this.base.getActiveFixingData(activeFixingId);
    } else {
      if (hasActiveFixing) {
        if (!this.base.hasOrderIdInActiveFixing(activeFixingId, orderId)) {
          this.base.addOrderToActiveFixing(activeFixingId, orderId, orderData);
        }

        this.base.moveFixingTo(activeFixingId, BmarkFixingType.RECENT);
        operation = BmarkFixingEventOperation.MOVE_TO_RECENT;
      } else if (hasRecentFixing) {
        if (!this.base.hasOrderIdInRecentFixing(recentFixingId, orderData.orderId.toString())) {
          this.base.addOrderToRecentFixing(recentFixingId, orderData.orderId.toString(), orderData);
        } else {
          this.base.updateOrderInRecentFixing(
            recentFixingId,
            orderData.orderId.toString(),
            orderData,
          );
        }
        operation = BmarkFixingEventOperation.UPDATE_DATA;
      } else {
        const fixingData = BmarkFixingData.create(
          orderData.fixingSourceCd,
          orderData.fixingDate,
          BmarkFixingType.RECENT,
          fixingDef,
        );
        this.base.addRecentFixing(fixingData);
        this.base.addOrderToRecentFixing(recentFixingId, orderData.orderId.toString(), orderData);
        this.allBenchmarksRequestService.addRecentFixing(fixingData);
        operation = BmarkFixingEventOperation.ADD;
      }
      fixingData = this.base.getRecentFixingData(recentFixingId);
    }

    if (operation != null && fixingData != null) {
      logger.out.info(
        `${this.constructor.name}.createFixingsFromOrderData() ` +
          `BmarkFixingEventOperation.${BmarkFixingEventOperation[operation]} ${fixingData.id} clientOrdersCount:${fixingData.clientOrdersCount} hedgeOrdersCount:${fixingData.hedgeOrdersCount}`,
      );
      this.fireBmarkFixingEvent(operation, fixingData);
    } else {
      logger.out.info(`${this.constructor.name}.createFixingsFromOrderData() no-op`);
    }
    return retFixingId;
  }

  private hasBmarkFixingData(orderData: CubiqTS.OrderDataImpl) {
    return (
      orderData instanceof CubiqTS.OrderDataImpl &&
      typeof orderData.fixingSourceCd === "string" &&
      orderData.fixingDate != null &&
      orderData.orderId instanceof CubiqTS.Long &&
      (CubiqTS.OrderType.BMALGO === orderData.orderType ||
        (CubiqTS.OrderType.BENCHMARK === orderData.orderType &&
          orderData.fixingDetails.bmOrderType === CubiqTS.BenchmarkOrderType.PositionOrder))
    );
  }

  /* ******************************************************* */
  /* FIXING PROCESSING - UPDATE */

  private processFixingUpdate(fixingEvent: CubiqTS.FixingEventImpl): void {
    if (fixingEvent instanceof CubiqTS.FixingEventImpl) {
      if (fixingEvent.bMTimeCodes instanceof Array && fixingEvent.bMTimeCodes.length > 0) {
        // just a check, can be removed
        for (let bMTimeCode of fixingEvent.bMTimeCodes) {
          if (bMTimeCode instanceof CubiqTS.BMTimeCodeImpl && bMTimeCode.time != null) {
            let activeFixingId = BmarkFixingData.generateFixingIdFromCodeMoment(
              bMTimeCode.code,
              bMTimeCode.time,
              BmarkFixingType.ACTIVE,
            );
            let recentFixingId = BmarkFixingData.generateFixingIdFromCodeMoment(
              bMTimeCode.code,
              bMTimeCode.time,
              BmarkFixingType.RECENT,
            );
            if (!this.shouldBePurgedOffset(bMTimeCode.time.format("x"))) {
              let fixingData = this.base.getFixingData(activeFixingId);
              let fixingDataRecent = this.base.getFixingData(recentFixingId);

              if (fixingData != null && fixingEvent.lastRevalTime != null) {
                fixingData.lastRevalTime = fixingEvent.lastRevalTime;
              }

              if (fixingData instanceof BmarkFixingData) {
                this.logBmarkInfo(
                  `${this.constructor.name} UPDATE => Fixing [${activeFixingId}] state updated (${
                    CubiqTS.FixingState[fixingData.state]
                  }) => (${CubiqTS.FixingState[fixingEvent.fixingState]})`,
                );
                // Update state
                fixingData.state = fixingEvent.fixingState;

                if (this.base.hasRecentFixing(recentFixingId)) {
                  logger.out.error(
                    `${this.constructor.name}.processFixingUpdate() fixing in both ACTIVE and RECENT, with a move to RECENT indicated so deleting from Active`,
                  );
                  this.base.deleteActiveFixing(activeFixingId);
                } else if (
                  this.base.hasRecentFixing(recentFixingId) === false &&
                  fixingData.state === CubiqTS.FixingState.COMPLETED
                ) {
                  this.base.moveFixingTo(activeFixingId, BmarkFixingType.RECENT);
                  this.logBmarkInfo(
                    `${this.constructor.name} UPDATE => Moving [${activeFixingId}] to RECENT due to FixingState.COMPLETED`,
                  );
                  this.fireBmarkFixingEvent(BmarkFixingEventOperation.MOVE_TO_RECENT, fixingData);
                } else {
                  // TODO: remove when server fixed
                  if (
                    fixingData.type === BmarkFixingType.ACTIVE &&
                    (fixingData.state === CubiqTS.FixingState.COMPLETED ||
                      fixingData.state === CubiqTS.FixingState.TRIGGERED ||
                      fixingData.state === CubiqTS.FixingState.FIXING_HIT)
                  ) {
                    logger.out.warn(
                      `${
                        this.constructor.name
                      }.processFixingUpdate() potential state mismatch OMS-32325 ${
                        fixingData.id
                      } CubiqTS.FixingState.${CubiqTS.FixingState[fixingData.state]}`,
                    );
                  }

                  this.fireBmarkFixingEvent(BmarkFixingEventOperation.UPDATE_DATA, fixingData);
                }
                // TODO: implement here expiredOrdersIds
                if (
                  fixingEvent.fixingState === CubiqTS.FixingState.COMPLETED &&
                  Array.isArray(fixingEvent.expiringOrderIds) &&
                  fixingEvent.expiringOrderIds.length > 0
                ) {
                  this.processFixingCompleteEventForExpiredOrders(fixingEvent);
                }
              } else if (
                fixingDataRecent instanceof BmarkFixingData &&
                fixingEvent.fixingState === CubiqTS.FixingState.COMPLETED
              ) {
                this.processFixingCompleteEventForExpiredOrders(fixingEvent);
              } else {
                // not an error, just a warning some update events should be ignored
                //this.logBmarkInfo(`WARN: ${this.constructor.name}.updateFixingsStatus(): BmarkFixingData for '${fixingId}' not found`);
              }
            } else if (this.base.hasFixingId(activeFixingId)) {
              if (this.checkPurgeFixingData(this.base.getFixingData(activeFixingId))) {
              }
            } else {
              this.logBmarkInfo(
                `${this.constructor.name}.updateFixingsStatus(): Ignoring update for [${activeFixingId}], because out of date`,
              );
            }
          } else {
            logger.out.error(
              `ERROR: ${this.constructor.name}.updateFixingsStatus(): Invalid BMTimeCodeImpl`,
            );
          }
        }
      }
    } else {
      logger.out.error(
        `ERROR: ${this.constructor.name}.updateFixingsStatus(): Invalid FixingEventImpl`,
      );
    }
  }

  protected processUnsolCanLifecycleEvent(event: CubiqTS.UnsolicitedCancelLifeCycleEventImpl): any {
    if (event.benchmarkFg == true) {
      return;
    }
    if (event.orders != null) {
      event.orders.forEach((incoming: CubiqTS.OrderData) => {
        // NOTIFICATIONS - Flow Control variables
        if (event._extendedInfo.get("makerId") != null) {
          let makerId = event._extendedInfo.get("makerId");
          let makerIdNum: number = +makerId;
          incoming.deskId = new CubiqTS.Long(makerIdNum);
        }
        if (this.activeUser.canSeeHedgeOrder(incoming)) {
          let notifType: BFXNotificationType = BFXNotificationType.UNSOLICITED_CANCEL;
          let shouldNotifyFg: boolean = this.notifService.shouldNotify(
            notifType,
            incoming.orderType,
          );
          let makerName: string = event._extendedInfo.get("maker");
          let opsId: string = event._extendedInfo.get("opsId");
          if (shouldNotifyFg) {
            this.notifService.fireNotificationEvent(
              this.notifService.orderDataToUnsolCanNotification(
                incoming,
                notifType,
                event.alternateGroupId?.toString() ?? "",
                event.alternateGroupName,
                makerName,
                opsId,
              ),
              BFXNotificationEventType.ORDER_UPDATE_EVENT,
            );
          } else {
            this.notifService.fireNotificationClearEvent(
              this.notifService.dataToClearEvent([incoming.orderId.toString()]),
              BFXNotificationEventType.ORDER_UPDATE_EVENT,
            );
          }
        }
      });
    }
  }

  protected processOrderDataUpdateForFixings(
    orderData: CubiqTS.OrderDataImpl,
    deleteOpr: boolean = false,
  ): void {
    let activeFixingId = BmarkFixingData.generateFixingIdFromOrderData(
      orderData,
      BmarkFixingType.ACTIVE,
    );
    let recentFixingId = BmarkFixingData.generateFixingIdFromOrderData(
      orderData,
      BmarkFixingType.RECENT,
    );

    if (activeFixingId != null && recentFixingId != null) {
      let isCreateActiveFixing =
        !deleteOpr &&
        !this.base.hasActiveFixing(activeFixingId) &&
        !this.base.hasRecentFixing(recentFixingId);
      let isUpdateActiveFixing = this.base.hasActiveFixing(activeFixingId);
      let isUpdateRecentFixing = this.base.hasRecentFixing(recentFixingId);

      if (isCreateActiveFixing) {
        let fixingId = this.createFixingsFromOrderData(orderData); // handles updates on its own
        this.retrieveUpdateForFixingId(fixingId);
      } else if (deleteOpr) {
        if (this.base.hasOrderIdInActiveFixing(activeFixingId, orderData.orderId.toString())) {
          logger.out.warn(
            `${this.constructor.name}.processOrderDataUpdateForFixings() isFillBasedRecent:true deleteOpr:true fixingId:${activeFixingId}`,
          );
          let pendingDeleteActiveFixing = this.base.getActiveFixingData(activeFixingId);
          this.base.deleteOrderIdInActiveFixing(activeFixingId, orderData.orderId.toString());

          this.allBenchmarksRequestService.invalidateById(activeFixingId);

          if (pendingDeleteActiveFixing.hasOrders) {
            // Send UPDATE event if other type of order still present
            this.fireBmarkFixingEvent(
              BmarkFixingEventOperation.UPDATE_DATA,
              this.base.getActiveFixingData(activeFixingId),
            );
          } else {
            // Send REMOVE event if last order id
            this.base.deleteActiveFixing(activeFixingId);

            this.fireBmarkFixingEvent(BmarkFixingEventOperation.REMOVE, pendingDeleteActiveFixing);
          }
        }
        if (this.base.hasOrderIdInRecentFixing(recentFixingId, orderData.orderId.toString())) {
          logger.out.warn(
            `${this.constructor.name}.processOrderDataUpdateForFixings() isFillBasedRecent:true deleteOpr:true fixingId:${recentFixingId}`,
          );
          let pendingDeleteRecentFixing = this.base.getRecentFixingData(recentFixingId);
          this.base.deleteOrderIdInRecentFixing(recentFixingId, orderData.orderId.toString());
          this.allBenchmarksRequestService.invalidateById(recentFixingId);
          if (pendingDeleteRecentFixing.hasOrders) {
            // Send UPDATE event
            this.fireBmarkFixingEvent(
              BmarkFixingEventOperation.UPDATE_DATA,
              this.base.getRecentFixingData(recentFixingId),
            );
          } else {
            this.base.deleteRecentFixing(recentFixingId);
            // Send REMOVE event
            this.fireBmarkFixingEvent(BmarkFixingEventOperation.REMOVE, pendingDeleteRecentFixing);
          }
        }
      } else if (isUpdateActiveFixing) {
        // Add Order id
        this.allBenchmarksRequestService.invalidateById(activeFixingId);

        if (!this.base.hasOrderIdInActiveFixing(activeFixingId, orderData.orderId.toString()))
          this.base.addOrderToActiveFixing(activeFixingId, orderData.orderId.toString(), orderData);
        else
          this.base.updateOrderInActiveFixing(
            activeFixingId,
            orderData.orderId.toString(),
            orderData,
          );

        this.fireBmarkFixingEvent(
          BmarkFixingEventOperation.UPDATE_DATA,
          this.base.getActiveFixingData(activeFixingId),
        );
      } else if (isUpdateRecentFixing) {
        let recentFixing = this.base.getRecentFixingData(recentFixingId);
        this.allBenchmarksRequestService.invalidateById(recentFixingId);

        if (!this.base.hasOrderIdInRecentFixing(activeFixingId, orderData.orderId.toString()))
          this.base.addOrderToRecentFixing(recentFixingId, orderData.orderId.toString(), orderData);
        else
          this.base.updateOrderInRecentFixing(
            recentFixingId,
            orderData.orderId.toString(),
            orderData,
          );

        recentFixing.updateFillStatusMessage(
          orderData,
          this.traderGroupService.getTraderGroup(orderData.deskId),
        );
        this.fireBmarkFixingEvent(
          BmarkFixingEventOperation.UPDATE_DATA,
          this.base.getRecentFixingData(recentFixingId),
        );
      } else {
        logger.out.error(
          `ERROR: ${this.constructor.name}.processOrderDataUpdateForFixings(): Invalid operation: `,
          orderData,
        );
      }
    } else {
      logger.out.error(
        `ERROR: ${this.constructor.name}.processOrderDataUpdateForFixings(): Invalid orderData: `,
        orderData,
      );
    }
  }

  /* ******************************************************* */
  /* FIXING DEFINITION MAP (BenchmarkFixingImpl)*/
  protected createFixingDefinitionMap(): Promise<boolean> {
    return new Promise<boolean>((res, rej) => {
      // try{
      // reset map
      this.fixingDefinitionMap = new Map<string, BmarkFixingDefinition>();

      this.fixingDefinitionDataService.isPrimed().then(() => {
        let fixingDefinitionsImpls = Array.from(
          this.fixingDefinitionDataService.getBase().getAll().values(),
        );
        for (let fixingDefinitionImpl of fixingDefinitionsImpls) {
          if (fixingDefinitionImpl instanceof CubiqTS.BenchmarkFixingImpl) {
            let fixingDefinition = new BmarkFixingDefinition(fixingDefinitionImpl);
            this.fixingDefinitionMap.set(fixingDefinition.code, fixingDefinition);
          }
        }

        res(true);
      });
      // } catch(ex){
      //     logger.out.error(`ERROR: ${this.constructor.name} Failed to create fixing definition map:`, ex);
      //     rej(ex);
      // }
    });
  }

  protected updateFixingDefinitionMap(fixingDefinitionImpl: CubiqTS.BenchmarkFixingImpl): void {
    // try{
    if (fixingDefinitionImpl instanceof CubiqTS.BenchmarkFixingImpl) {
      let fixingDefinition = new BmarkFixingDefinition(fixingDefinitionImpl);
      this.fixingDefinitionMap.set(fixingDefinition.code, fixingDefinition);
      this.logBmarkInfo(`CubiqTS.BenchmarkFixingImpl updated in map`, this.fixingDefinitionMap);
    }
    // }catch(ex){
    //     logger.out.error(`ERROR: ${this.constructor.name} Failed to update fixing definition item:`, ex);
    // }
  }

  protected getFixingDefinitionCopy(code: string): BmarkFixingDefinition {
    return this.fixingDefinitionMap.has(code)
      ? Object.assign(new BmarkFixingDefinition(), this.fixingDefinitionMap.get(code))
      : null;
  }

  /*
   * FIXING PURGE PROCESSING - 3 TYPES of PURGE:
   * checkPurgeOrderDataOnFixingRemoved() -> Deletes orderData for recent fixings orders that are not displayed
   * checkAllPurgeRecentFixings() -> Deletes Recent fixings older than X hours => no longer in view recent fixings list
   * checkAllSweepActiveToRecent() -> Moves Active Fixing to Recent List after sweep time passed.
   */

  protected scheduleCheckRecentOrdersPurge(): void {
    setInterval(() => {
      //logger.out.info(`${this.constructor.name}: ${this.constructor.name} THE PURGE TAKING PLACE NOW!`)
      this.checkPurgeOrderDataOnFixingRemoved();
      // this.checkAllSweepActiveToRecent();
    }, this.fixingsRecentPurgeInterval);
  }

  protected checkPurgeOrderDataOnFixingRemoved(): void {
    let recentFixings = this.base.getAllRecentFixings();

    if (recentFixings instanceof Array && recentFixings.length > 0) {
      for (let recentFixing of recentFixings) {
        if (
          !this.checkPurgeFixingData(recentFixing) &&
          recentFixing.hasHedgeOrders() &&
          this.shouldBePurgedIdle(recentFixing.id, recentFixing.purgeTimestamp)
        ) {
          this.base.clearOrderIdsInRecentFixing(recentFixing.id);
          for (let orderId of recentFixing.getHedgeOrderIds()) {
            this.base.deleteItemById(orderId);
            this.fireModelDeleteEvent(orderId);
          }

          this.logBmarkInfo(
            `${this.constructor.name} Purging IDLE Fixing [${recentFixing.id}] orders`,
          );
        }
      }
    }
  }

  private shouldBePurgedIdle(fixingId: string, purgeTimestamp: number | string): boolean {
    try {
      purgeTimestamp =
        typeof purgeTimestamp === "string" ? parseInt(purgeTimestamp) : purgeTimestamp;
      let currentTimestamp = parseInt(CubiqTS.moment().format("x"));
      return (
        fixingId !== this.displayedRecentFixing &&
        currentTimestamp >= purgeTimestamp + this.fixingsRecentOrdersIdleOffset
      );
    } catch (ex) {
      logger.out.error(`${this.constructor.name}.shouldBePurgedIdle() execption`, ex);
      return false;
    }
  }

  protected checkAllPurgeRecentFixings(): void {
    let recentFixings = this.base.getAllRecentFixings();
    if (recentFixings instanceof Array && recentFixings.length > 0) {
      for (let recentFixing of recentFixings) {
        this.checkPurgeFixingData(recentFixing);
      }
    }
  }

  protected checkPurgeFixingData(fixingData: BmarkFixingData): boolean {
    if (fixingData instanceof BmarkFixingData) {
      let fixingId = fixingData.id;
      let fixingType = fixingData.type;
      if (this.shouldBePurgedOffset(fixingData.fixingTimestamp)) {
        fixingType === BmarkFixingType.RECENT
          ? this.base.deleteRecentFixing(fixingId)
          : this.base.deleteActiveFixing(fixingId);
        this.logBmarkInfo(
          `${this.constructor.name} Fixing [${fixingId}] of ${BmarkFixingType[fixingType]} PURGED - too old to live!`,
        );
        this.fireBmarkFixingEvent(BmarkFixingEventOperation.REMOVE, fixingData);

        return true;
      }
    }
    return false;
  }

  private shouldBePurgedOffset(fixingTimestamp: number | string): boolean {
    try {
      fixingTimestamp =
        typeof fixingTimestamp === "string" ? parseInt(fixingTimestamp) : fixingTimestamp;
      let currentTimestamp = parseInt(CubiqTS.moment().format("x"));
      return currentTimestamp >= fixingTimestamp + this.fixingsRecentPurgeOffset;
    } catch (ex) {
      return false;
    }
  }

  protected checkAllSweepActiveToRecent(): void {
    let activeFixings = this.base.getAllActiveFixings();
    if (activeFixings instanceof Array && activeFixings.length > 0) {
      for (let activeFixing of activeFixings) {
        this.sweepFixingData(activeFixing);
      }
    }
  }

  protected sweepFixingData(fixingData: BmarkFixingData): boolean {
    if (fixingData instanceof BmarkFixingData) {
      if (!this.isOrderDataActive(fixingData.fixingTimestamp, fixingData.code)) {
        this.base.moveFixingTo(fixingData.id, BmarkFixingType.RECENT);
        this.logBmarkInfo(
          `${this.constructor.name} SWEEP => Moving [${fixingData.id}] to RECENT due to fixing Data should be swept`,
        );
        this.fireBmarkFixingEvent(BmarkFixingEventOperation.MOVE_TO_RECENT, fixingData);
        return true;
      }
    }

    return false;
  }

  protected isOrderDataActive(fixingTimestamp: number, code: string): boolean {
    let sweepTimeOffset: number = this.defaultSweepOffset;
    let currentTimestamp = parseInt(CubiqTS.moment().format("x"));
    let isActive: boolean = false;
    let fixingDefinition: BmarkFixingDefinition = this.fixingDefinitionMap.get(code);

    if (fixingDefinition != null) {
      sweepTimeOffset = fixingDefinition.sweepTimeOffset;

      if (typeof sweepTimeOffset === "string") sweepTimeOffset = parseInt(sweepTimeOffset);

      if (typeof sweepTimeOffset !== "number" || isNaN(sweepTimeOffset))
        sweepTimeOffset = this.defaultSweepOffset;
    }

    isActive = currentTimestamp < fixingTimestamp + sweepTimeOffset;

    return isActive;
  }

  /* ******************************************************* */
  /* BENCHMARK FIXINGS - REPORTING */

  private reportFixingsListSummary(): void {
    let recentFixings = this.base.getAllRecentFixings();
    let recentCount = 0;
    for (let recentFixing of recentFixings) {
      recentCount = recentCount + recentFixing.hedgeOrdersCount;
    }
    this.logBmarkInfo(
      `${this.constructor.name} RECENT Fixings: ${recentFixings
        .map((item) => item.id)
        .toString()} | total orders: ${recentCount}`,
    );

    // Active fixings report - to be deleted
    let activeFixings = this.base.getAllActiveFixings();
    let activeCountOrders = 0;
    let activeCountHCO = 0;
    let activeCountSCO = 0;
    for (let activeFixing of activeFixings) {
      activeCountOrders = activeCountOrders + activeFixing.hedgeOrdersCount;
      activeCountHCO =
        activeFixing.state === CubiqTS.FixingState.HARD_CUTOFF
          ? activeCountHCO + 1
          : activeCountHCO;
      activeCountSCO =
        activeFixing.state === CubiqTS.FixingState.SOFT_CUTOFF
          ? activeCountSCO + 1
          : activeCountSCO;
    }
    this.logBmarkInfo(
      `${this.constructor.name} ACTIVE Fixings: ${activeFixings
        .map((item) => item.id)
        .toString()} | ` +
        `HCO (${activeCountHCO}) => [${
          activeCountHCO > 0
            ? activeFixings
                .filter((item) => item.state === CubiqTS.FixingState.HARD_CUTOFF)
                .map((item) => item.id)
            : "N/A"
        }] | ` +
        `SCO (${activeCountSCO}) => [${
          activeCountSCO > 0
            ? activeFixings
                .filter((item) => item.state === CubiqTS.FixingState.SOFT_CUTOFF)
                .map((item) => item.id)
            : "N/A"
        }] | ` +
        `total orders: ${activeCountOrders}`,
    );

    let duplicates = this.checkDuplicateIds();
    if (duplicates.length > 0) {
      this.logBmarkInfo(`${this.constructor.name} DUPLICATE FIXINGS: `, duplicates);
    }
  }

  private checkDuplicateIds(): Array<string> {
    let activeFixingsIds = this.base.getAllActiveFixingsIds();
    let recentFixingsIds = this.base.getAllRecentFixingsIds();
    let duplicates = new Array<string>();
    if (recentFixingsIds instanceof Array && recentFixingsIds.length > 0) {
      for (let recentFixingId of recentFixingsIds) {
        let result = activeFixingsIds.find((item) => item === recentFixingId);
        if (result != null) {
          duplicates.push(recentFixingId);
        }
      }
    }
    return duplicates;
  }

  /* ******************************************************* */

  @ModelUpdate(CubiqTS.ClientImpl)
  protected onClientUpdate(client: CubiqTS.ClientImpl, order: CubiqTS.OrderDataImpl): boolean {
    return order.clientCode == client.clientId;
  }

  @ModelUpdate(CubiqTS.AccountImpl)
  protected onAccountUpdate(account: CubiqTS.AccountImpl, order: CubiqTS.OrderDataImpl): boolean {
    return order.accountCode == account.id;
  }

  @ModelUpdate(CubiqTS.CurrencyImpl)
  protected onCurrencyUpdate(
    currency: CubiqTS.CurrencyImpl,
    order: CubiqTS.OrderDataImpl,
  ): boolean {
    return order.baseCode == currency.id || order.nonBaseCode == currency.id;
  }

  @ModelUpdate(CubiqTS.CurrencyPairImpl)
  protected onCurrencyPairUpdate(
    currencyPair: CubiqTS.CurrencyPairImpl,
    order: CubiqTS.OrderDataImpl,
  ): boolean {
    return order.baseCode == currencyPair.base && order.nonBaseCode == currencyPair.nonBase;
  }

  @ModelUpdate(CubiqTS.TradingGroupImpl)
  protected onTraderGroupUpdate(
    traderGroup: CubiqTS.TradingGroupImpl,
    order: CubiqTS.OrderDataImpl,
  ): boolean {
    return (
      this.compareIds(order.deskId, traderGroup.groupId) ||
      this.compareIds(order.targetDeskId, traderGroup.groupId)
    );
  }

  @ModelUpdate(CubiqTS.SalesGroupImpl)
  protected onSalesGroupUpdate(
    salesGroup: CubiqTS.SalesGroupImpl,
    order: CubiqTS.OrderDataImpl,
  ): boolean {
    return this.compareIds(order.salesGroupId, salesGroup.groupId);
  }

  @ModelUpdate(CubiqTS.UserImpl)
  protected onUserUpdate(user: CubiqTS.UserImpl, order: CubiqTS.OrderDataImpl): boolean {
    return (
      this.compareIds(order.entryUserId, user.userId) ||
      this.compareIds(order.lockUserId, user.userId) ||
      this.compareIds(order.transferUserId, user.userId)
    );
  }

  /* ******************************************************* */
  /* BMARK ORDER-DATA UPDATE PRIMARY SUBJECT */

  public getBmarkOrderDataUpdateSubject(): Subject<any> {
    return this.bmarkOrderDataUpdateSubject;
  }

  public getInterventionEventObservable(): Observable<CubiqTS.InterventionEventImpl> {
    return this.interventionEventSubject.asObservable();
  }

  /* ******************************************************* */
  /* BMARK ORDER-DATA UPDATE PRIMARY SUBJECT */

  private processForPrivatization(
    orderData: Array<CubiqTS.OrderDataImpl> | CubiqTS.OrderDataImpl,
  ): void {
    let orderDataArray = null;
    if (orderData instanceof CubiqTS.OrderDataImpl) {
      orderDataArray = new Array<CubiqTS.OrderDataImpl>();
      orderDataArray.push(orderData);
    } else if (Array.isArray(orderData) && orderData.length > 0) {
      orderDataArray = orderData;
    }

    if (orderDataArray != null) {
      for (let orderDataItem of orderDataArray) {
        this.privatizationService.checkPrivatization(orderDataItem);
      }
    }
  }

  /* ******************************************************* */
  /* BMARK ORDER-DATA UPDATE EVENT SENDERS */
  /* Not in use */
  /*     protected fireBmarkBatchOrderDataUpdateEvent(batchUpdate: Array<any>): any {
        if (batchUpdate instanceof Array && batchUpdate.length > 0) {

            let $event = { subjectType: BmarkEventSubjectType.ORDER_DATA, data: batchUpdate};
            this.bmarkOrderDataUpdateSubject.next($event.data);
            this.logBmarkInfo(`${this.constructor.name} Sending BATCH OrderData update... ${BmarkOrderDataEventOperation[BmarkOrderDataEventOperation.BATCH_UPDATE]} => `, $event);
        }else{
           logger.out.error(`ERROR: ${this.constructor.name} fireFixingUpdateEvent(): Invalid BATCH update data:`, batchUpdate);
        }
    } */

  protected fireBmarkOrderDataUpdateEvent(orderDataEvent: BmarkOrderDataEvent): any {
    // operations: Array<BmarkOrderDataEventOperation>, bmarkOrderData: CubiqTS.OrderDataImpl | null, metaData: any
    if (
      orderDataEvent instanceof BmarkOrderDataEvent &&
      orderDataEvent.payload instanceof BmarkOrderDataEventPayload &&
      Array.isArray(orderDataEvent.operations) &&
      orderDataEvent.operations.length > 0
    ) {
      this.logBmarkInfo(
        `${this.constructor.name} Sending BMARK-ORDER-DATA-EVENT ` +
          `(${
            orderDataEvent.payload.source in BmarkOrderDataEventSource
              ? BmarkOrderDataEventSource[orderDataEvent.payload.source]
              : "N/A"
          })` +
          ` update => operations: ${orderDataEvent.operations.map(
            (item) => BmarkOrderDataEventOperation[item],
          )} => `,
        orderDataEvent,
      );
      this.bmarkOrderDataUpdateSubject.next(orderDataEvent);
    } else {
      logger.out.error(
        `ERROR: ${this.constructor.name} fireFixingUpdateEvent(): Invalid order-data-event => `,
        orderDataEvent,
      );
    }
  }

  /* ******************************************************* */
  /* ORDER DATA IMPL SERIALIZER */
  protected serializeOrderDataInUpdateEvent($event: any): void {
    // try{
    if (Array.isArray($event)) {
      // for batched events
      for (let $eventItem of $event) {
        this.serializeOrderDataInUpdateEvent($eventItem);
      }
    } else {
      if (
        $event != null &&
        $event.data != null &&
        $event.data.payload != null &&
        $event.data.payload.orderData instanceof CubiqTS.OrderDataImpl
      ) {
        $event.data.payload.orderData = this.control.attachImplToEvent
          ? this._serializer.serialize($event.data.payload.orderData, this.VALUE)
          : null;
      }
    }
    // }catch(ex){
    //     logger.out.error(`ERROR: ${this.constructor.name} serializeOrderDataInUpdateEvent(): `, ex);
    // }
  }

  /* ******************************************************* */
  /* BMARK ORDER DATA EVENT HANDLERS */
  protected processBmarkOrderLifecycleEvent(
    event: CubiqTS.OrderLifeCycleEventImpl,
    returnDoNotSend: boolean = false,
  ): BmarkOrderDataEvent {
    //this.lifeCycleTimerRestart();
    logger.out.info(
      `${this.constructor.name}.processBmarkOrderLifecycleEvent(): OrderLifeCycleEventImpl orderId: ${event?.orderData?.orderId}`,
    );

    if (event instanceof CubiqTS.OrderLifeCycleEventImpl) {
      // try {
      if (
        event.orderData != null &&
        this.shouldProcessUpdate(event.orderData as CubiqTS.OrderDataImpl)
      ) {
        /* ORDER DATA */
        let orderDataUpdate: BmarkOrderData | null =
          event.orderData != null
            ? this.base.preprocessOrderData(event.orderData as CubiqTS.OrderDataImpl)
            : null;
        let orderDataStored: BmarkOrderData = this.base.getBmarkOrderDataForOrderId(
          orderDataUpdate.orderId.toString(),
        );
        /* EVENT */
        let orderDataEvent = new BmarkOrderDataEvent();
        /*************************************/
        /* PROCESSING  */
        this.privatizationService.checkPrivatization(orderDataUpdate);
        // process lock
        orderDataUpdate.lockType =
          orderDataUpdate.lockType === CubiqTS.LockType.CANCEL_LOCK
            ? null
            : orderDataUpdate.lockType;
        /*************************************/
        /* FLOW CONTROL */
        // UPDATE/DELETE
        // #bmarkVisibility

        const recentFixingId = BmarkFixingData.generateFixingIdFromOrderData(
          orderDataUpdate,
          BmarkFixingType.RECENT,
        );
        let hasRecentFixing = this.base.hasRecentFixing(recentFixingId);

        let isInRejectedList = HEDGE_ORDER_REJECT_STATES.has(orderDataUpdate.orderState);
        let isInActiveList = HEDGE_ORDER_ACTIVE_STATES.has(orderDataUpdate.orderState);
        let canSeeBmarkOrder = this.activeUser.canSeeBmarkHedgeOrder(orderDataUpdate, false);

        // NOTIFICATIONS - Flow Control variables
        let determinedNotifTypes = this.notificationProcessor.determineNotifType(
          event,
          orderDataStored,
        );
        let appNotifType: BFXAppNotificationType = determinedNotifTypes.appType;

        // TODO Following two flags need to be revised for Benchmark based notifications
        // let shouldNotifyFg:boolean = this.shouldNotify(notifType);
        // let shouldAppNotifyFg:boolean = this.shouldAppNotify(appNotifType);
        let shouldNotifyFg: boolean = true;
        let shouldAppNotifyFg: boolean = true;

        // ORDER ENTRY - Flow Control variables
        let shouldSendOEClear =
          orderDataUpdate.orderState === CubiqTS.OrderState.IN_MODIFICATION ||
          orderDataUpdate.orderState === CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED ||
          orderDataUpdate.orderState === CubiqTS.OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED;

        let isReject = event.eventTypeId === CubiqTS.FxLifeCycleEventType.REJECTED;
        let isRollbackEvent = event.rollbackEventFg;

        ///////////////////////////////////////////////////// DELETE FLOW
        if (!canSeeBmarkOrder || isInRejectedList || (!isInActiveList && !hasRecentFixing)) {
          /*************************************/
          /* UPDATE LOOKUP DATA */
          // perform delete if it exists

          if (orderDataUpdate instanceof CubiqTS.OrderDataImpl) {
            // update fixings
            this.processOrderDataUpdateForFixings(orderDataUpdate, true);
            // update map
            this.base.deleteItemById(orderDataUpdate.orderId.toString());
            this.fireModelDeleteEvent(orderDataUpdate.orderId.toString());
          } else {
            logger.out.warn(
              `${this.constructor.name}.processBmarkOrderLifecycleEvent() failed to update fixing for order`,
            );
          }
          /*************************************/
          /* DEFINE EVENT */
          /* define meta data */
          let eventPayloadData = new BmarkOrderDataEventPayload(
            orderDataUpdate.orderId.toString(),
            event.externalClientReference,
            BmarkOrderDataEventSource.OrderLifeCycleEvent,
            orderDataUpdate,
          );

          /* define operations */
          orderDataEvent.addOperations(BmarkOrderDataEventOperation.DELETE); // for blotters
          if (!isRollbackEvent) {
            // for order entry
            if (isReject) {
              eventPayloadData.errors = event.errors;
              orderDataEvent.addOperations(BmarkOrderDataEventOperation.OE_ERROR);
            } else if (HEDGE_ORDER_NEW_ORDER_STATES.has(orderDataUpdate.orderState)) {
              orderDataEvent.addOperations(BmarkOrderDataEventOperation.OE_DI_NEEDED);
            }
          }
          /* attach payload */
          orderDataEvent.payload = eventPayloadData;
          /*************************************/
          /* NOTIFICATION EVENT */
          if (orderDataStored != null) {
            if (shouldNotifyFg) {
              // this.fireModelNotificationEvent(orderDataStored,{notifType:notifType,notifSubType:notifSubType});
            } else {
              this.fireModelNotificationClearEvent([orderDataUpdate.orderId.toString()]);
            }
            if (shouldAppNotifyFg) {
              this.fireAppModelNotificationEvent(orderDataStored, {
                appNotifType: appNotifType,
              });
            }
          }

          ///////////////////////////////////////////////////// UPDATE FLOW
        } else {
          /*************************************/
          /* UPDATE LOOKUP DATA */
          switch (event.fxOrderLifeCycleEvent) {
            case CubiqTS.FxLifeCycleEventType.MODIFICATION_INFO: // MODIFY INFO
              if (orderDataStored != null) {
                orderDataStored.comments1 = orderDataUpdate.comments1;
                orderDataStored.comments2 = orderDataUpdate.comments2;
                orderDataStored.clientTraderName = orderDataUpdate.clientTraderName;
              }
              break;
            case CubiqTS.FxLifeCycleEventType.CREDIT_UPDATE:
              if (orderDataStored != null) {
                orderDataStored.creditRevalDetails = orderDataUpdate.creditRevalDetails;
                this.base.updateItem(orderDataStored);
              }
              break;
            case CubiqTS.FxLifeCycleEventType.PAUSED: {
              if (orderDataStored != null) {
                orderDataStored.pauseState = CubiqTS.PauseState.PAUSED;
                this.base.updateItem(orderDataStored);
              }
              break;
            }
            case CubiqTS.FxLifeCycleEventType.RESUMED: {
              if (orderDataStored != null) {
                orderDataStored.pauseState = CubiqTS.PauseState.NOT_PAUSED;
                this.base.updateItem(orderDataStored);
              }
              break;
            }
            default:
              orderDataStored = orderDataUpdate;
              this.base.mapItem(orderDataStored);
              if (!shouldNotifyFg) {
                this.fireModelNotificationClearEvent([orderDataUpdate.orderId.toString()]);
              }
              break;
          }
          // update fixings
          if (isInActiveList || hasRecentFixing) {
            this.processOrderDataUpdateForFixings(orderDataUpdate);
          }

          /*************************************/
          /* META-DATA PREPARATION */
          let lockUser: any = null;
          if (shouldSendOEClear) {
            lockUser = orderDataUpdate.lockUserId ? orderDataUpdate.lockUserId.toString() : null;
          } else if (CubiqTS.FxLifeCycleEventType.SI_REQUIRED && orderDataUpdate.siLockUserId) {
            lockUser = orderDataUpdate.siLockUserId.toString();
          } else if (orderDataUpdate.lockUserId) {
            lockUser = orderDataUpdate.lockUserId.toString();
          }

          // traderGroups
          let traderGroupIds: Array<string> = new Array<string>();
          if (!shouldSendOEClear) {
            if (orderDataUpdate.deskId != null) {
              traderGroupIds.push(orderDataUpdate.deskId.toString());
            }
            if (
              traderGroupIds.length === 0 &&
              Array.isArray(orderDataUpdate.ownerGroupIds) &&
              orderDataUpdate.ownerGroupIds[0] instanceof CubiqTS.Long
            ) {
              traderGroupIds = orderDataUpdate.ownerGroupIds.map((item) => item.toString());
            }
          }
          /*************************************/
          /* DEFINE EVENT */
          /* define payload */
          let eventPayloadData = new BmarkOrderDataEventPayload(
            orderDataUpdate.orderId.toString(),
            event.externalClientReference != null
              ? event.externalClientReference
              : orderDataUpdate.clOrdID,
            BmarkOrderDataEventSource.OrderLifeCycleEvent,
            orderDataUpdate,
          );

          // bmark order data
          eventPayloadData.bmarkOrderData =
            this.base.processLocalBmarkOrderDataForVisibility(orderDataUpdate); //orderDataUpdate;
          // meta-data
          eventPayloadData.state = orderDataUpdate.orderState;
          eventPayloadData.evtType = event.eventTypeId;
          eventPayloadData.messageId = event.messageId;
          eventPayloadData.sgcId =
            !shouldSendOEClear && orderDataUpdate.salesGroupId != null
              ? orderDataUpdate.salesGroupId.toString()
              : null;
          eventPayloadData.tgcId = traderGroupIds;
          (eventPayloadData.deskCode =
            !shouldSendOEClear && Array.isArray(traderGroupIds) && traderGroupIds.length > 0
              ? traderGroupIds.map((item) => {
                  let tgc = this.traderGroupService.getItem(item.toString());
                  return tgc instanceof CubiqTS.TradingGroupImpl ? tgc.groupCode : item.toString();
                })
              : new Array<string>()),
            (eventPayloadData.user = lockUser);
          eventPayloadData.isCreditUpdate =
            event.eventTypeId == CubiqTS.FxLifeCycleEventType.CREDIT_UPDATE;
          eventPayloadData.isRollbackEvent = event.rollbackEventFg;
          // errors
          eventPayloadData.errors = event.errors;
          // leave it up to UI service to add/remove as doesn't know about active/recent selection in ui
          // eventPayloadData.isFillBasedActiveToRecentMove = isFillBasedRecent;

          /* define operations */
          orderDataEvent.addOperations(BmarkOrderDataEventOperation.UPDATE);
          if (shouldSendOEClear) {
            orderDataEvent.addOperations(BmarkOrderDataEventOperation.OE_CLEAR);
          }

          /* attach payload */
          orderDataEvent.payload = eventPayloadData;
          /*************************************/
          /* FIRE NOTIFICATION EVENT */
          if (shouldAppNotifyFg) {
            this.fireAppModelNotificationEvent(orderDataStored, {
              appNotifType: appNotifType,
            });
          }
        }
        /*************************************/
        /* FIRE BMARK EVENT */
        if (!returnDoNotSend) {
          this.fireBmarkOrderDataUpdateEvent(orderDataEvent);
          // for dialogs like cancel -dc
          // this.fireModelUpdateEvent(event.orderData);
          // fireModelUpdateEvent is broken from here?
          let eventObject: any = Object.assign(
            Object.create(BmarkOrderData.prototype),
            orderDataStored,
            {
              source: OrderDataModelUpdateSource.OrderLifeCycleEvent,
              evtTypeId: event.eventTypeId,
            },
          );
          if (
            event.errors != null &&
            Array.isArray(event.errors.errorMessages) &&
            event.errors.errorMessages.length > 0
          ) {
            eventObject.orderErrors = event.errors;
          }
          this.modelUpdateEventSubject.next(eventObject);
        }

        return orderDataEvent;
      } // should not have been processed -
      // } catch (error) {
      //     logger.out.error('Failed to propagate OrderLifeCycleEvent event.', error);
      // }
    } else {
      logger.out.error("Invalid OrderLifeCycleEvent event.", event);
    }
    return null;
  }

  /* BMARK LOCK EVENT HANDLER */
  protected processBmarkLockLifecycleEvent(event: CubiqTS.BenchmarkLockLifeCycleEventImpl): any {
    if (event instanceof CubiqTS.BenchmarkLockLifeCycleEventImpl) {
      // try{
      if (
        event.lockType != CubiqTS.LockType.CANCEL_LOCK &&
        event.orderIds != null &&
        Array.isArray(event.orderIds)
      ) {
        let batchUpdate: Array<BmarkOrderDataEvent> = new Array<BmarkOrderDataEvent>();

        for (let orderId of event.orderIds) {
          /* ORDER-DATA */
          let orderDataStored: BmarkOrderData = this.base.getBmarkOrderDataForOrderId(
            orderId.toString(),
          );
          /* EVENT  */
          let orderDataEvent = new BmarkOrderDataEvent();
          /* PROCESSING  */
          if (orderDataStored instanceof CubiqTS.OrderDataImpl) {
            /*************************************/
            /* UPDATE LOOKUP DATA */
            if (
              event.abort === true ||
              event.eventTypeId === CubiqTS.FxLifeCycleEventType.UNLOCKED
            ) {
              // UNLOCK
              orderDataStored.lockType = null;
              orderDataStored.lockUserId = null;
              //                                orderDataStored.orderState = CubiqTS.OrderState.ACTIVE; // NB: may not have been active (Pending, Ext Accept Needed, etc)
            } else {
              // LOCK
              orderDataStored.lockType = event.lockType;
              orderDataStored.lockUserId = event.lockUserId;
              // if(event.lockType == CubiqTS.LockType.MODIFY_LOCK) {
              //   orderDataStored.orderState = CubiqTS.OrderState.IN_MODIFICATION;
              // }
            }
            this.base.updateItem(orderDataStored);

            /*************************************/
            /* DEFINE EVENT */
            let eventPayloadData = new BmarkOrderDataEventPayload(
              orderId.toString(),
              orderDataStored.clOrdID,
              BmarkOrderDataEventSource.BmarkLockLifeCycleEvent,
              orderDataStored,
            );
            // order data
            eventPayloadData.bmarkOrderData = orderDataStored;
            eventPayloadData.state = orderDataStored.orderState;
            // meta-data
            eventPayloadData.evtType = event.eventTypeId;
            eventPayloadData.messageId = event.messageId;
            eventPayloadData.user = event.lockUserId ? event.lockUserId.toString() : null;
            eventPayloadData.lockEvent = true;

            /* define operations */
            orderDataEvent.addOperations([
              BmarkOrderDataEventOperation.UPDATE,
              BmarkOrderDataEventOperation.LOCK_UPDATE,
            ]);
            /* attach payload */
            orderDataEvent.payload = eventPayloadData;

            /*************************************/
            /* FIRE BMARK UPDATE EVENT */
            this.fireBmarkOrderDataUpdateEvent(orderDataEvent);
          }
        }
      }

      // } catch (error) {
      //     logger.out.error('Failed to propagate BenchmarkLockLifeCycleEventImpl event.', error);
      // }
    } else {
      logger.out.error("Invalid BenchmarkLockLifeCycleEventImpl event.", event);
    }
  }

  /* TRANSFER EVENT HANDLER */
  protected processBmarkXferEvent(event: CubiqTS.XferEventImpl): void {
    if (event instanceof CubiqTS.XferEventImpl) {
      // try{
      let clearNotifs: Array<string> = [];
      this.logBmarkInfo(`${this.constructor.name}: XferEventImpl.count[${event.orderData.length}]`);

      for (let orderData of event.orderData) {
        if (orderData != null && this.shouldProcessUpdate(orderData as CubiqTS.OrderDataImpl)) {
          /* ORDER DATA */
          let orderDataUpdate: BmarkOrderData | null =
            orderData != null
              ? this.base.preprocessOrderData(orderData as CubiqTS.OrderDataImpl)
              : null;
          /* EVENT  */
          let orderDataEvent = new BmarkOrderDataEvent();
          /* PROCESSING  */
          this.privatizationService.checkPrivatization(orderDataUpdate);

          /*************************************/
          /* FLOW CONTROL */
          // UPDATE/DELETE
          let recentFixingId = BmarkFixingData.generateFixingIdFromOrderData(
            orderDataUpdate,
            BmarkFixingType.RECENT,
          );

          let hasRecentFixing = this.base.hasRecentFixing(recentFixingId);

          let isInRejectedList = HEDGE_ORDER_REJECT_STATES.has(orderDataUpdate.orderState);

          let isInActiveList = HEDGE_ORDER_ACTIVE_STATES.has(orderDataUpdate.orderState);
          let canSeeBmarkOrder = this.activeUser.canSeeBmarkHedgeOrder(orderDataUpdate, false);
          let isDeleted = false;

          /*************************************/
          /* META-DATA PREPARATION */
          let username: string = null;
          if (event.actioningUserId != null) {
            let user: UserImpl = this.userService.getUserById(event.actioningUserId.toString());
            username = user != null ? user.userName : username;
          }

          let targetGroup: string = null;
          if (event.targetGroupId != null) {
            let tradeGroup: TradingGroupImpl = this.traderGroupService.getItem(
              event.targetGroupId.toString(),
            );
            targetGroup = tradeGroup != null ? tradeGroup.groupCode : targetGroup;
          }

          /*************************************/
          /* DEFINE EVENT */
          let eventPayloadData = new BmarkOrderDataEventPayload(
            orderDataUpdate.orderId.toString(),
            orderDataUpdate.clOrdID,
            BmarkOrderDataEventSource.XferEvent,
            orderDataUpdate,
          );
          // order data
          eventPayloadData.bmarkOrderData = orderDataUpdate;
          // meta-data
          eventPayloadData.messageId = event.messageId;
          eventPayloadData.xferEventType = event.eventType;
          eventPayloadData.xferComment = event.comment;
          eventPayloadData.xferUserName = username;
          eventPayloadData.xferTargetGroup = targetGroup;

          if (!canSeeBmarkOrder || isInRejectedList || (!isInActiveList && !hasRecentFixing)) {
            /*************************************/
            /* UPDATE LOOKUP DATA */
            if (this.base.hasItem(orderDataUpdate.orderId.toString())) {
              // perform delete if it exists
              // update fixings
              this.processOrderDataUpdateForFixings(orderDataUpdate, true);
              // update map
              this.base.deleteItemById(orderDataUpdate.orderId.toString());
            }

            eventPayloadData.deleteReason = TRANSFERED_OUT_OF_VISIBILITY;
            /* define operations */
            orderDataEvent.addOperations(BmarkOrderDataEventOperation.DELETE);
            isDeleted = true;
          } else {
            /*************************************/
            /* UPDATE LOOKUP DATA */
            this.base.mapItem(orderDataUpdate as CubiqTS.OrderDataImpl);
            // update fixings
            if (isInActiveList) {
              this.processOrderDataUpdateForFixings(orderDataUpdate);
            }

            // order data update with visibility - fix for
            eventPayloadData.bmarkOrderData =
              this.base.processLocalBmarkOrderDataForVisibility(orderDataUpdate);
            this.devTestDisplayOrderData([eventPayloadData.bmarkOrderData]);
            /* define operations */
            orderDataEvent.addOperations(BmarkOrderDataEventOperation.UPDATE);
          }
          /* attach payload */
          orderDataEvent.payload = eventPayloadData;
          /*************************************/
          /* FIRE BMARK EVENT */
          this.fireBmarkOrderDataUpdateEvent(orderDataEvent);

          if (isDeleted) {
            this.fireModelDeleteEvent(orderDataUpdate.orderId.toString());
          } else {
            this.fireModelUpdateEventForTransfer(
              eventPayloadData.bmarkOrderData,
              event,
              username,
              targetGroup,
            );
          }
        }
      } // for end
      /*************************************/
      /* FIRE BMARK BATCH UPDATE EVENT */
      if (clearNotifs.length > 0) {
        this.fireModelNotificationClearEvent(clearNotifs);
      }

      // } catch (error) {
      //     logger.out.error('Failed to propagate processXferEvent event.', error);
      // }
    } else {
      logger.out.error("Invalid processXferEvent event.", event);
    }
  }

  private fireModelUpdateEventForTransfer(
    bmarkOrderData: BmarkOrderData,
    event: CubiqTS.XferEventImpl,
    username: string,
    targetGroup: string,
  ) {
    let eventObject: any = Object.assign(Object.create(BmarkOrderData.prototype), bmarkOrderData, {
      source: OrderDataModelUpdateSource.XferEvent,
      evtTypeId: event.eventType,
    });
    eventObject.xferEventType = event.eventType;
    eventObject.xferComment = event.comment;
    eventObject.xferUserName = username;
    eventObject.xferTargetGroup = targetGroup;
    this.modelUpdateEventSubject.next(eventObject);
  }

  /* BULK LIFE-CYCLE EVENT HANDLER */
  protected processBmarkBulkOrderLifecycleEvent(event: CubiqTS.BulkOrderLifeCycleEventImpl): void {
    if (event instanceof CubiqTS.BulkOrderLifeCycleEventImpl) {
      // try{

      for (let iEvent of event.events) {
        if (iEvent instanceof CubiqTS.OrderLifeCycleEventImpl && this.benchmarksFilter(iEvent)) {
          this.processBmarkOrderLifecycleEvent(iEvent);
        }
      }
      // } catch (error) {
      //     logger.out.error('Failed to propagate BulkOrderLifeCycleEventImpl event.', error);
      // }
    } else {
      logger.out.error("Invalid BulkOrderLifeCycleEventImpl event.", event);
    }
  }

  /* INTERVENTION EVENT HANDLER */
  protected processBmarkInterventionEvent($event: CubiqTS.InterventionEventImpl): void {
    if ($event instanceof CubiqTS.InterventionEventImpl) {
      // try{
      if (
        $event.orderData instanceof CubiqTS.OrderDataImpl &&
        ($event.eventType !== CubiqTS.InterventionEventType.LIMIT_ORDER ||
          $event.orderData.orderType == CubiqTS.OrderType.BMALGO)
      ) {
        if ($event.orderData && $event.orderData instanceof CubiqTS.OrderDataImpl) {
          $event.orderData = this.privatizationService.checkPrivatization(
            $event.orderData as CubiqTS.OrderDataImpl,
          );
        }

        /*************************************/
        /* ORDER DATA */
        let orderDataUpdate: BmarkOrderData | null =
          $event.orderData != null
            ? this.base.preprocessOrderData($event.orderData as CubiqTS.OrderDataImpl)
            : null;
        /* EVENT */
        let orderDataEvent = new BmarkOrderDataEvent();

        /*************************************/
        /* FLOW CONTROL */
        let isEvtTypeNew = $event.operationType === CubiqTS.InterventionOperationType.NEW;
        let isEvtTypeApprove = $event.operationType === CubiqTS.InterventionOperationType.APPROVE;
        let isEvtTypeReject = $event.operationType === CubiqTS.InterventionOperationType.REJECT;

        /*************************************/
        /* META-DATA PREPARATION */
        let traderGroupCodeId =
          $event.orderData != null && $event.orderData.deskId != null
            ? $event.orderData.deskId.toString()
            : null;
        if (
          traderGroupCodeId == null &&
          Array.isArray($event.orderData.allocationData) &&
          $event.orderData.allocationData.length > 0
        ) {
          // first allocation TG
          traderGroupCodeId =
            $event.orderData.allocationData[0].deskId instanceof CubiqTS.Long
              ? $event.orderData.allocationData[0].deskId.toString()
              : null;
        }
        let salesGroupCodeId =
          $event.orderData != null && $event.orderData.salesGroupId != null
            ? $event.orderData.salesGroupId.toString()
            : null;
        let lockUser = $event.lockUserId != null ? $event.lockUserId.toString() : null;

        let orderDataStored: BmarkOrderData = this.base.getBmarkOrderDataForOrderId(
          orderDataUpdate.orderId.toString(),
        );
        if (null != orderDataStored && null == lockUser) {
          orderDataStored.lockUserId = null;
          orderDataStored.lockType = null;
          this.base.updateItem(orderDataStored);
        }
        /*************************************/
        /* EVENT DEFINITION */
        let eventPayloadData = new BmarkOrderDataEventPayload(
          orderDataUpdate.orderId.toString(),
          orderDataUpdate.clOrdID,
          BmarkOrderDataEventSource.InterventionEvent,
          orderDataUpdate,
        );
        // order data
        eventPayloadData.bmarkOrderData = orderDataStored;
        //
        eventPayloadData.tgcId = isEvtTypeNew ? traderGroupCodeId : null;
        eventPayloadData.sgcId = isEvtTypeNew ? salesGroupCodeId : null;
        eventPayloadData.user = isEvtTypeNew ? lockUser : null;
        eventPayloadData.invEvtType = $event.eventType;
        eventPayloadData.invOprType = $event.operationType;
        eventPayloadData.reasonId = $event.reason;
        eventPayloadData.unlockUserId =
          $event.unLockUserId != null ? $event.unLockUserId.toString() : null;
        eventPayloadData.messageId = $event.messageId;
        eventPayloadData.errors = $event.errors;
        /* attach payload */
        orderDataEvent.payload = eventPayloadData;

        /* define operations */
        // order entry + blotters
        if (isEvtTypeNew) {
          orderDataEvent.addOperations(BmarkOrderDataEventOperation.OE_DI_NEW);
        } else if (isEvtTypeApprove) {
          orderDataEvent.addOperations(BmarkOrderDataEventOperation.OE_DI_APPROVE);
        } else if (isEvtTypeReject) {
          orderDataEvent.addOperations(BmarkOrderDataEventOperation.OE_DI_REJECT);
        }

        /*************************************/
        /* FIRE BMARK EVENT */
        this.fireBmarkOrderDataUpdateEvent(orderDataEvent);
      } else {
        logger.out.error(
          `ERROR: ${this.constructor.name} Intervention event does not have Order Data %O`,
          $event,
        );
      }
      // } catch (ex) {
      //     logger.out.error('ERROR: ${this.constructor.name} Failed to propagate InterventionEventImpl event. ', ex);
      // }
    } else {
      logger.out.error(
        `ERROR: ${this.constructor.name} Invalid InterventionEventImpl event. %O`,
        $event,
      );
    }
  }

  /* FIXING ORDER UPDATE EVENT HANDLER (Fill Error)*/
  protected processBmarkFixingOrderUpdate($event: CubiqTS.FixingOrderUpdateEventImpl) {
    if (
      $event instanceof CubiqTS.FixingOrderUpdateEventImpl &&
      typeof $event.orderId === "string"
    ) {
      // try{
      if ($event.orderState === CubiqTS.OrderState.FILL_ERROR) {
        /* ORDER DATA */
        let orderDataStored: BmarkOrderData = this.base.getBmarkOrderDataForOrderId($event.orderId);
        /* EVENT */
        let orderDataEvent = new BmarkOrderDataEvent();

        if (orderDataStored != null) {
          /*************************************/
          /* UPDATE LOOKUP DATA */
          orderDataStored.orderState = $event.orderState;
          this.base.updateItem(orderDataStored);

          /*************************************/
          /* DEFINE EVENT */
          /* define meta data */
          let eventPayloadData = new BmarkOrderDataEventPayload(
            orderDataStored.orderId.toString(),
            orderDataStored.clOrdID,
            BmarkOrderDataEventSource.OrderLifeCycleEvent,
            orderDataStored,
          );
          // bmark order data
          eventPayloadData.bmarkOrderData = orderDataStored;
          eventPayloadData.lockEvent = true;
          /* define operations */
          orderDataEvent.addOperations(BmarkOrderDataEventOperation.UPDATE);

          /* attach payload */
          orderDataEvent.payload = eventPayloadData;
          /*************************************/
          /* FIRE BMARK UPDATE EVENT */
          this.fireBmarkOrderDataUpdateEvent(orderDataEvent);
        }
      } else {
        logger.out.warn(
          `WARN: ${this.constructor.name} Received FixingOrderUpdateEventImpl with state other than FILL_ERROR`,
          $event,
        );
      }
      // }catch(ex){
      //     logger.out.error('ERROR: ${this.constructor.name} Failed to propagate FixingOrderUpdateEventImpl event. ', ex);
      // }
    }
  }

  protected processFixingCompleteEventForExpiredOrders(fixingEvent: CubiqTS.FixingEventImpl) {
    if (
      fixingEvent.fixingState === CubiqTS.FixingState.COMPLETED &&
      Array.isArray(fixingEvent.expiringOrderIds) &&
      fixingEvent.expiringOrderIds.length > 0
    ) {
      // Update order state to expired
      this.base.updateOrderDataState(fixingEvent.expiringOrderIds, CubiqTS.OrderState.FILL_EXPIRED);

      let orderDataUpdateForFixings: Array<BmarkOrderData> = [];

      // Create and dispatch OrderData events
      for (let orderId of fixingEvent.expiringOrderIds) {
        let castOrderId =
          orderId instanceof CubiqTS.Long
            ? orderId.toString()
            : typeof orderId === "string"
            ? orderId
            : null;
        if (this.base.hasItem(castOrderId)) {
          /* ORDER DATA */
          let orderDataStored: BmarkOrderData = this.base.getBmarkOrderDataForOrderId(castOrderId);
          logger.out.info(
            `${this.constructor.name}: Order state (${castOrderId}) update to ${
              CubiqTS.OrderState[orderDataStored.orderState]
            }`,
          );
          /* EVENT */
          let orderDataEvent = new BmarkOrderDataEvent();

          /*************************************/
          /* META-DATA PREPARATION */
          let traderGroupIds: Array<string> = new Array<string>();
          if (orderDataStored.deskId != null) {
            traderGroupIds.push(orderDataStored.deskId.toString());
          }
          if (
            traderGroupIds.length === 0 &&
            Array.isArray(orderDataStored.ownerGroupIds) &&
            orderDataStored.ownerGroupIds[0] instanceof CubiqTS.Long
          ) {
            traderGroupIds = orderDataStored.ownerGroupIds.map((item) => item.toString());
          }

          /*************************************/
          /* DEFINE EVENT */
          /* define meta data */
          let eventPayloadData = new BmarkOrderDataEventPayload(
            orderDataStored.orderId.toString(),
            orderDataStored.clOrdID,
            BmarkOrderDataEventSource.OrderLifeCycleEvent,
            orderDataStored,
          );
          // bmark order data
          eventPayloadData.bmarkOrderData = orderDataStored;

          eventPayloadData.state = orderDataStored.orderState;
          eventPayloadData.evtType = CubiqTS.FxLifeCycleEventType.FILL_EXPIRED;
          eventPayloadData.messageId = fixingEvent.messageId;
          eventPayloadData.tgcId = traderGroupIds;
          (eventPayloadData.sgcId =
            orderDataStored.salesGroupId != null ? orderDataStored.salesGroupId.toString() : null),
            (eventPayloadData.deskCode =
              Array.isArray(traderGroupIds) && traderGroupIds.length > 0
                ? traderGroupIds.map((item) => {
                    let tgc = this.traderGroupService.getItem(item.toString());
                    return tgc instanceof CubiqTS.TradingGroupImpl
                      ? tgc.groupCode
                      : item.toString();
                  })
                : new Array<string>());

          /* define operations */
          orderDataEvent.addOperations(BmarkOrderDataEventOperation.UPDATE);

          /* attach payload */
          orderDataEvent.payload = eventPayloadData;

          orderDataUpdateForFixings.push(orderDataEvent.payload.bmarkOrderData);

          /* FIRE BMARK EVENT */
          this.fireBmarkOrderDataUpdateEvent(orderDataEvent);
        } else {
          logger.out.error(
            `ERROR: ${this.constructor.name}: processFixingCompleteEventForExpiredOrders() Order not found: ${castOrderId}`,
          );
        }
      }

      orderDataUpdateForFixings.forEach((orderData: BmarkOrderData) => {
        this.processOrderDataUpdateForFixings(orderData);
      });
    }
  }

  /* ******************************************************* */
  /* EVENT QUEUING */

  /* FIXING EVENTS */
  protected flushQueuedFixingUpdateEvents(): void {
    this.control.queueFixingEvents = false;
    this.logBmarkInfo(`Fixing event queueing STOPPED! Queue size: ${this.fixingEventQueue.length}`);
    if (this.fixingEventQueue && this.fixingEventQueue.length > 0) {
      this.logBmarkInfo(`Flushing ${this.fixingEventQueue.length} queued fixing events.`);
      this.fixingEventQueue.forEach((queuedEvent: HedgeOrderQueuedEvent) => {
        // try {
        if (queuedEvent instanceof HedgeOrderQueuedEvent) {
          queuedEvent.processEventFunction(queuedEvent.event);
        }
        // } catch (error) {
        //     logger.out.error("Failed to process queued fixing event", queuedEvent, error);
        // }
      });
      this.fixingEventQueue = [];
    }
  }

  /* ORDER DATA EVENTS */
  protected flushQueuedBmarkOrderDataEvents(): void {
    this.control.queueOrderDataEvents = false;
    this.logBmarkInfo(
      `Bmark Order Data event queueing STOPPED! Queue size: ${this.orderDataEventQueue.length}`,
    );
    if (Array.isArray(this.orderDataEventQueue) && this.orderDataEventQueue.length > 0) {
      this.logBmarkInfo(
        `Flushing ${this.orderDataEventQueue.length} queued Bmark Order Data events.`,
        null,
        true,
      );
      this.orderDataEventQueue.forEach((queuedEvent: HedgeOrderQueuedEvent) => {
        // try {
        let fixingId = BmarkFixingData.generateFixingIdFromOrderData(
          queuedEvent.event.orderData,
          BmarkFixingType.ALL,
        );
        this.logBmarkInfo(`Processing Queued ORDER DATA Event for fixing ${fixingId}`, null, true);
        queuedEvent.processEventFunction(queuedEvent.event);
        // } catch (error) {
        //     logger.out.error("Failed to process queued order data update event", queuedEvent, error);
        // }
      });
      this.orderDataEventQueue = [];
    }
  }

  /* ******************************************************* */
  /* ORDER DATA EVENT HANDLER HELPERS */

  protected shouldProcessUpdate(order: CubiqTS.OrderDataImpl): boolean {
    // try {
    let oldItem: CubiqTS.OrderDataImpl = this.base.getItem(this.base.getItemKey(order));
    if (oldItem != null && order != null && order.txTime != null && oldItem.txTime != null) {
      let process: boolean = order.txTime.isSameOrAfter(oldItem.txTime);

      if (!process && order.orderId != null) {
        logger.out.warn(
          `${
            this.constructor.name
          }: Skipping order data update for order ${order.orderId.toString()} message ID ${
            order.messageId
          }.`,
        );
      }

      return process;
    }
    // } catch (error) {
    //     logger.out.warn("Error determining if an order update should be processed. Update will be processed.", order, error);
    // }
    return order != null;
  }

  /* ******************************************************* */
  /*  NOTIFICATION TYPE HANDLING */

  public getAllAllocationDatasetForOrder(
    orderData: CubiqTS.OrderDataImpl | BmarkOrderData,
  ): Set<CubiqTS.AllocationData> {
    return this.base.getAllAllocationDatasetForOrder(orderData);
  }

  /* ******************************************************* */
  /*  DEBUG - LOG */

  protected logBmarkInfo(
    message: string,
    dumpData: any = null,
    warning: boolean = false,
    error: boolean = false,
  ): void {
    try {
      if (this.outputLog === false) {
        dumpData = null;
      }
      let prefix = `${this.constructor.name} ${error ? "ERROR: " : warning ? "WARN: " : ":"}`;
      if (error) {
        if (dumpData != null) {
          logger.out.error(`${prefix} ${typeof message === "string" ? message : ""} %O`, dumpData);
        } else {
          logger.out.error(`${prefix} ${typeof message === "string" ? message : ""}`);
        }
      } else if (warning) {
        if (dumpData != null) {
          logger.out.warn(`${prefix} ${typeof message === "string" ? message : ""} %O`, dumpData);
        } else {
          logger.out.warn(`${prefix} ${typeof message === "string" ? message : ""}`);
        }
      } else if (dumpData != null) {
        logger.out.info(`${prefix} ${typeof message === "string" ? message : ""} %O`, dumpData);
      } else {
        logger.out.info(`${prefix} ${typeof message === "string" ? message : ""}`);
      }
    } catch (ex) {
      logger.out.error(`WORKER-ERROR: Extended logging exception (Other): %O`, ex);
    }
  }

  public getActiveFixingData(fixingId: string): BmarkFixingData {
    return this.base.getActiveFixingData(fixingId);
  }

  public isRoutedExternally(orderData: CubiqTS.OrderData): boolean {
    let errors = orderData ? orderData.orderErrors : null;
    let isRoutedExternally = false;

    if (
      errors instanceof CubiqTS.ErrorsImpl &&
      Array.isArray(errors.errorMessages) &&
      errors.errorMessages.length > 0
    ) {
      let errorMessages = errors.errorMessages;
      for (let message of errorMessages) {
        let errorMessageJson: string = message as any;
        let errorMessage = { id: null };
        try {
          errorMessage = JSON.parse(errorMessageJson);
        } catch {
          /* no-op */
        }

        if (
          typeof errorMessage === "object" &&
          errorMessage.hasOwnProperty("id") &&
          "horse" === errorMessage.id
        ) {
          isRoutedExternally = true;
        }
      }
    }

    return isRoutedExternally;
  }

  public onOrderFlash(orderId: string): void {
    let orderData: BmarkOrderData = this.base.getBmarkOrderDataForOrderId(orderId, true, true);
    if (orderData != null && this.activeUser.canSeeBmarkHedgeOrder(orderData, false)) {
      let orderDataEvent = new BmarkOrderDataEvent();

      let eventPayloadData = new BmarkOrderDataEventPayload(
        orderData.orderId.toString(),
        orderData.clOrdID,
        BmarkOrderDataEventSource.OrderLifeCycleEvent,
        orderData,
      );

      eventPayloadData.bmarkOrderData = this.base.processLocalBmarkOrderDataForVisibility(
        orderData,
        true,
      ); //orderDataUpdate;
      eventPayloadData.state = orderData.orderState;
      orderDataEvent.addOperations(BmarkOrderDataEventOperation.UPDATE);
      orderDataEvent.payload = eventPayloadData;
      this.fireBmarkOrderDataUpdateEvent(orderDataEvent);
    }
  }

  /**
   * Checks orders in memory that need to have their inflight state changed and publish a notification
   * to refresh the order data in the blotter.
   * @param message the inflight check status change containing the new infligh check state and the order IDs affected
   */
  protected processInflightApplicationMessage(message: CubiqTS.ApplicationMessageEventImpl): void {
    try {
      if (
        message.messageType === EVENT_TYPE_INFLIGHT_CHECK_STATE &&
        message.message instanceof CubiqTS.InflightCheckStateEventImpl
      ) {
        logger.out.info(
          `${this.constructor.name}: Processing inflight change state ${message.message}`,
        );
        let event: CubiqTS.InflightCheckStateEventImpl = message.message;
        if (event.orderIds != null && event.orderIds.length > 0) {
          let orders: BmarkOrderData[] = event.orderIds
            .map((o) => this.base.getBmarkOrderDataForOrderId(o.toString()))
            .filter((o) => o != null);

          orders.forEach((o) => {
            let order: CubiqTS.OrderDataImpl = this.base.getItem(o.orderId.toString());
            order.inflightInclusionState = event.inflightInclusionState;
            this.onOrderFlash(o.orderId.toString());
          });

          logger.out.info(
            `${this.constructor.name}: ${orders.length} hedge order processed for inflight state change our of ${event.orderIds.length} total.`,
          );
        }
      }
    } catch (e) {
      logger.out.error(
        `${this.constructor.name}: Failed to process application message event: ${e}`,
      );
    }
  }
}
